import { useState, useEffect } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  DollarSign, Users, MousePointer, TrendingUp, Copy, ExternalLink, 
  Award, Mail, Lock, LogIn, LogOut, Home, FileText, Share2, 
  UserPlus, Gift, LayoutDashboard, Image, CreditCard, RefreshCw,
  CheckCircle, CheckCircle2, Clock, AlertCircle, BarChart3, Globe, Sparkles, Target,
  Wand2, Palette, Eye, EyeOff, Save, Loader2, ChevronDown, ChevronUp, Trash2,
  ImageIcon, Facebook, Instagram, Linkedin, MessageSquare, Building
} from 'lucide-react';

import { API_URL } from '../config';
const API = `${API_URL}/api`;


function PayoutHistory({ affiliateCode, API }) {
  const [payouts, setPayouts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!affiliateCode) return;
    fetch(`${API}/affiliate/payouts/${affiliateCode}`)
      .then(r => r.json())
      .then(d => { setPayouts(d.payouts || []); setLoading(false); })
      .catch(() => setLoading(false));
  }, [affiliateCode, API]);

  if (loading) return <p className="text-sm text-slate-500">Loading payout history...</p>;
  if (!payouts.length) return <p className="text-sm text-slate-500">No payout requests yet.</p>;

  return (
    <div className="space-y-3" data-testid="payout-history">
      {payouts.map((p, i) => (
        <div key={p.payout_id || i} className="flex items-center justify-between p-3 border rounded-lg">
          <div>
            <span className="font-semibold">${p.amount?.toFixed(2)}</span>
            <span className="text-sm text-slate-500 ml-2">via {p.payout_method || 'PayPal'}</span>
            <span className="text-xs text-slate-400 ml-2">{new Date(p.requested_at).toLocaleDateString()}</span>
          </div>
          <span className={`text-xs px-2 py-1 rounded-full font-medium ${
            p.status === 'paid' ? 'bg-green-100 text-green-700' :
            p.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
            'bg-red-100 text-red-700'
          }`}>
            {p.status}
            {p.rejection_reason && <span className="ml-1">- {p.rejection_reason}</span>}
          </span>
        </div>
      ))}
    </div>
  );
}

function AffiliateDashboard() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const codeFromUrl = searchParams.get('code');
  const [affiliateCode, setAffiliateCode] = useState(codeFromUrl || localStorage.getItem('affiliate_code'));
  const [affiliateToken, setAffiliateToken] = useState(localStorage.getItem('affiliate_token'));
  
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [copiedPartner, setCopiedPartner] = useState(false);
  const [activeTab, setActiveTab] = useState('dashboard');
  
  // Template analytics state
  const [templateAnalytics, setTemplateAnalytics] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [copiedLandingPage, setCopiedLandingPage] = useState(null);
  
  // OG A/B Testing analytics state
  const [ogAbAnalytics, setOgAbAnalytics] = useState(null);
  const [ogAbLoading, setOgAbLoading] = useState(false);
  
  // Detailed page analytics state
  const [detailedAnalytics, setDetailedAnalytics] = useState(null);
  const [detailedAnalyticsLoading, setDetailedAnalyticsLoading] = useState(false);
  const [analyticsPeriod, setAnalyticsPeriod] = useState(30); // days
  
  // Custom template generator state
  const [showTemplateGenerator, setShowTemplateGenerator] = useState(false);
  const [generatingTemplate, setGeneratingTemplate] = useState(false);
  const [customTemplateConfig, setCustomTemplateConfig] = useState({
    name: '',
    headline: '',
    subheadline: '',
    primaryColor: '#7c3aed',
    style: 'modern',
    tone: 'professional',
    keyBenefits: '',
    ctaText: 'Get Started Free'
  });
  const [generatedTemplatePreview, setGeneratedTemplatePreview] = useState(null);
  const [customTemplates, setCustomTemplates] = useState([]);
  const [savingTemplate, setSavingTemplate] = useState(false);
  
  // Document & Payment Setup State
  const [verificationStatus, setVerificationStatus] = useState(null);
  const [uploadingW9, setUploadingW9] = useState(false);
  const [uploadingId, setUploadingId] = useState(false);
  const [savingPayment, setSavingPayment] = useState(false);
  const [paymentForm, setPaymentForm] = useState({
    paypalEmail: '',
    preferredMethod: 'paypal'
  });
  
  // Login form state
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  
  // Leads management state
  const [leads, setLeads] = useState([]);
  const [leadsStats, setLeadsStats] = useState(null);
  const [leadsLoading, setLeadsLoading] = useState(false);
  const [leadsFilter, setLeadsFilter] = useState('all');
  
  // Landing pages management state
  const [myLandingPages, setMyLandingPages] = useState([]);
  const [landingPagesLoading, setLandingPagesLoading] = useState(false);
  const [submittingPage, setSubmittingPage] = useState(null);
  
  // OG Image selector state
  const [showOgSelector, setShowOgSelector] = useState(null); // page_id of page being edited
  const [ogOptions, setOgOptions] = useState({});
  const [selectedOgStyle, setSelectedOgStyle] = useState(null);
  const [savingOgStyle, setSavingOgStyle] = useState(false);
  
  // Social post templates state
  const [showSocialPosts, setShowSocialPosts] = useState(null); // template name being viewed
  const [copiedPost, setCopiedPost] = useState(null);
  
  // Social Media Post Templates - "So simple even a Sloth can do it!"
  const getSocialPosts = (templateName, affiliateLink) => {
    const posts = {
      'default': {
        facebook: `🤖 Did you know 60% of consumers now ask AI for business recommendations?

I just found this FREE tool that shows you if ChatGPT, Claude & other AI assistants are recommending YOUR business.

Takes 30 seconds. No credit card needed.

Check yours here 👇
${affiliateLink}

This could be a game-changer for any local business owner!`,
        instagram: `🚨 Is AI sending customers to your competitors? 🚨

60% of people now ask ChatGPT "Who's the best [business] near me?"

If you're not showing up... you're losing money you don't even know about 💸

Free tool to check your AI visibility in 30 seconds ⬇️ Link in bio!

${affiliateLink}

#AImarketing #SmallBusiness #LocalBusiness #ChatGPT #DigitalMarketing #BusinessGrowth #MarketingTips #Entrepreneur #BusinessOwner #FreeTools`,
        linkedin: `The way customers find businesses is changing rapidly.

📊 60% of consumers now use AI assistants (ChatGPT, Perplexity, Claude) to find recommendations for local services.

Here's what that means for business owners:
• If AI doesn't know you exist, you're invisible to these customers
• Your competitors who optimize for AI are capturing this traffic
• This trend is accelerating - 2026 will be the tipping point

I came across a free tool that actually queries AI assistants to see if they recommend your business. Takes 30 seconds.

Worth checking: ${affiliateLink}

Have you thought about how AI search is affecting your business?`
      },
      'sales-funnel': {
        facebook: `⚠️ While you're reading this, AI is sending YOUR customers to your competitors.

Every single day, millions of people ask ChatGPT: "Who's the best [your service] near me?"

If AI doesn't know your business exists... those customers go elsewhere.

FREE tool shows you exactly where you stand:
${affiliateLink}

The harsh truth: The businesses that optimize first will dominate. Don't get left behind! 🏃‍♂️`,
        instagram: `Your competitors are already doing this... are you? 👀

AI is the NEW way people find businesses:
✅ 60% use AI for recommendations
✅ $4.5 TRILLION influenced by AI in 2026
✅ First movers are DOMINATING

Don't wait until it's too late ⏰

Free AI visibility check (30 seconds):
${affiliateLink}

#BusinessStrategy #AISearch #CompetitiveAdvantage #SmallBusinessTips #MarketingStrategy #FutureOfBusiness #GetAhead #BusinessGrowth`,
        linkedin: `🚨 A shift is happening that most business owners are missing:

AI assistants are becoming the new "front door" for local businesses.

When someone asks ChatGPT "Who's the best accountant in Dallas?" - is YOUR firm being recommended?

The data is clear:
→ 60% of consumers now use AI for recommendations
→ 73% trust AI suggestions over traditional Google results
→ By end of 2026, AI will influence $4.5T in purchasing decisions

The businesses optimizing for AI visibility NOW are capturing customers their competitors don't even know exist.

Free tool to check where you stand: ${affiliateLink}

This isn't coming. It's here.`
      },
      'case-study': {
        facebook: `📈 Real story: A Texas RV dealer went from ZERO AI mentions to being recommended 73% of the time.

Result? $89,000 in new revenue in 60 days.

The crazy part? They didn't change their business at all - they just made sure AI knew they existed.

Want to see if AI recommends YOUR business? Free check here:
${affiliateLink}

This is probably the biggest opportunity in marketing right now that most people are ignoring. 🎯`,
        instagram: `From $0 to $89K in 60 days 🤯

How? One simple change...

A Texas business went from being INVISIBLE to AI...
To being recommended 73 out of 100 times!

The result? 💰💰💰

Want to see your AI visibility score?
Free tool (link in bio): ${affiliateLink}

#CaseStudy #BusinessSuccess #AIMarketing #RevenueGrowth #MarketingWins #SmallBusinessSuccess #RealResults #BusinessGrowth`,
        linkedin: `Case Study: How One Business Added $89,000 in Revenue Without Changing Their Product

A family-owned RV dealership in Texas was struggling with a problem they didn't even know they had:

When potential customers asked AI assistants for RV dealer recommendations in their area - they weren't being mentioned. Not once.

After optimizing for AI visibility:
📈 Before: 0/100 AI recommendations
📈 After 60 days: 73/100 AI recommendations  
💰 Revenue impact: +$89,000 attributed to AI-driven traffic

The fascinating part? The business, their products, and their service stayed exactly the same. They simply made themselves "visible" to AI systems.

Free tool to check your AI visibility: ${affiliateLink}

I'd love to hear - is AI visibility on your radar for 2026?`
      },
      'comparison': {
        facebook: `BEFORE: Business invisible to AI = Missing customers you'll never know about

AFTER 60 DAYS: AI recommends your business = New revenue on autopilot

See the transformation for yourself 👇

This free tool shows you EXACTLY how AI sees your business right now:
${affiliateLink}

The difference between "before" and "after" could be worth thousands. Just saying... 🤷‍♂️`,
        instagram: `BEFORE vs AFTER 📊

❌ Before: 0 AI recommendations
✅ After: 73/100 AI mentions

❌ Before: Losing customers to competitors
✅ After: AI sends customers to YOU

❌ Before: Invisible online
✅ After: $89K new revenue

See where YOUR business stands (free):
${affiliateLink}

#BeforeAndAfter #BusinessTransformation #AIMarketing #GrowthHacking #SmallBusiness #LocalBusiness #MarketingResults`,
        linkedin: `The Before & After That Changed My Perspective on Marketing

BEFORE optimization:
• 0 out of 100 AI queries mentioned the business
• Competitors ranked 1-3 in AI recommendations
• Unknown amount of lost opportunities

AFTER 60 days:
• 73 out of 100 AI queries now recommend them
• #1 in their category
• +340% increase in AI-driven traffic
• $89,000 in attributable new revenue

The gap between businesses that understand AI visibility and those that don't is widening every day.

Free tool to see your current state: ${affiliateLink}

Where does your business fall on this spectrum?`
      },
      'urgency': {
        facebook: `⏰ URGENT: Every day you wait, AI is sending YOUR customers to competitors who showed up first.

This isn't a future thing. It's happening RIGHT NOW.

60% of consumers already use AI for business recommendations. Are you showing up?

FREE analysis (usually $497) - limited time:
${affiliateLink}

Don't let another day pass without knowing where you stand! 🏃‍♂️💨`,
        instagram: `🚨 TIME SENSITIVE 🚨

While you scroll...
→ 1000s of people are asking AI for recommendations
→ AI is sending them to businesses that optimized
→ You might be losing customers RIGHT NOW

Don't wait until your competitors have all the AI traffic 😬

Free visibility check (limited time):
${affiliateLink}

#UrgentBusiness #ActNow #AIMarketing #DontWait #BusinessOpportunity #LimitedTime #FreeOffer #SmallBusiness`,
        linkedin: `🔔 A time-sensitive opportunity most business owners are missing:

Right now, as you read this:
• Thousands of potential customers are asking AI for recommendations
• AI is directing them to businesses that have optimized
• The first-mover advantage window is closing

The businesses that act NOW will establish themselves as the AI-recommended leaders in their markets. Those who wait will be playing catch-up.

For a limited time, you can get a FREE AI visibility analysis (normally $497):
${affiliateLink}

The question isn't IF AI will impact your business. It's whether you'll lead or follow.

What's your AI strategy?`
      }
    };
    
    return posts[templateName] || posts['default'];
  };
  
  const copyPostToClipboard = async (text, postType) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedPost(postType);
      setTimeout(() => setCopiedPost(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };
  
  // Email CRM state
  const [emailCampaigns, setEmailCampaigns] = useState([]);
  const [campaignsLoading, setCampaignsLoading] = useState(false);
  const [showNewCampaign, setShowNewCampaign] = useState(false);
  const [savingCampaign, setSavingCampaign] = useState(false);
  const [newCampaign, setNewCampaign] = useState({
    campaign_name: '',
    subject: '',
    body: '',
    delay_days: 0,
    is_active: true
  });
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [activatingTemplate, setActivatingTemplate] = useState(null);
  
  // Sub-affiliate source analytics state
  const [sourceAnalytics, setSourceAnalytics] = useState(null);
  const [sourceAnalyticsLoading, setSourceAnalyticsLoading] = useState(false);

  useEffect(() => {
    if (affiliateCode) {
      localStorage.setItem('affiliate_code', affiliateCode);
      fetchStats();
      fetchTemplateAnalytics();
      fetchDetailedAnalytics();
      fetchOgAbAnalytics();
      fetchCustomTemplates();
      fetchVerificationStatus();
      fetchLeads();
      fetchMyLandingPages();
      fetchEmailCampaigns();
      fetchEmailTemplates();
    } else {
      setLoading(false);
    }
  }, [affiliateCode]);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/affiliate/stats/${affiliateCode}`);
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
      handleLogout();
    } finally {
      setLoading(false);
    }
  };

  const fetchVerificationStatus = async () => {
    if (!affiliateCode) return;
    try {
      const response = await axios.get(`${API}/affiliate/${affiliateCode}/verification-status`);
      setVerificationStatus(response.data);
      // Pre-fill payment form if data exists
      if (response.data.paypal_info) {
        setPaymentForm(prev => ({
          ...prev,
          paypalEmail: response.data.paypal_info
        }));
      }
      if (response.data.preferred_payment_method) {
        setPaymentForm(prev => ({
          ...prev,
          preferredMethod: response.data.preferred_payment_method
        }));
      }
    } catch (error) {
      console.error('Error fetching verification status:', error);
    }
  };

  const fetchLeads = async () => {
    if (!affiliateCode) return;
    setLeadsLoading(true);
    try {
      const response = await axios.get(`${API}/affiliate/leads/${affiliateCode}`);
      setLeads(response.data.leads || []);
      setLeadsStats(response.data.stats || null);
    } catch (error) {
      console.error('Error fetching leads:', error);
    } finally {
      setLeadsLoading(false);
    }
  };

  const fetchMyLandingPages = async () => {
    if (!affiliateCode) return;
    setLandingPagesLoading(true);
    try {
      const response = await axios.get(`${API}/affiliate/landing-pages/${affiliateCode}`);
      setMyLandingPages(response.data.landing_pages || []);
    } catch (error) {
      console.error('Error fetching landing pages:', error);
    } finally {
      setLandingPagesLoading(false);
    }
  };

  const submitPageForApproval = async (pageId) => {
    setSubmittingPage(pageId);
    try {
      await axios.post(`${API}/affiliate/landing-page/submit/${pageId}`);
      await fetchMyLandingPages();
      alert('Page submitted for approval!');
    } catch (error) {
      console.error('Error submitting page:', error);
      alert(error.response?.data?.detail || 'Failed to submit page');
    } finally {
      setSubmittingPage(null);
    }
  };

  const fetchOgOptions = async (industry) => {
    try {
      const response = await axios.get(`${API}/og-styles/industry/${industry}`);
      setOgOptions(prev => ({
        ...prev,
        [industry]: response.data.styles
      }));
      return response.data.styles;
    } catch (error) {
      console.error('Error fetching OG options:', error);
      return [];
    }
  };

  const openOgSelector = async (page) => {
    setShowOgSelector(page.page_id);
    setSelectedOgStyle(page.og_style_id || null);
    
    // Fetch options for this industry if not already loaded
    if (!ogOptions[page.template_id]) {
      await fetchOgOptions(page.template_id);
    }
  };

  const saveOgStyle = async (pageId, styleId, templateId) => {
    setSavingOgStyle(true);
    try {
      await axios.post(`${API}/affiliate/landing-page/set-og-style?page_id=${pageId}&style_id=${styleId}&affiliate_code=${affiliateCode}`);
      await fetchMyLandingPages();
      setShowOgSelector(null);
      alert('OG image style saved! When your page is shared on social media, this image will appear.');
    } catch (error) {
      console.error('Error saving OG style:', error);
      alert(error.response?.data?.detail || 'Failed to save OG style');
    } finally {
      setSavingOgStyle(false);
    }
  };

  const fetchEmailCampaigns = async () => {
    if (!affiliateCode) return;
    setCampaignsLoading(true);
    try {
      const response = await axios.get(`${API}/affiliate/campaigns/${affiliateCode}`);
      setEmailCampaigns(response.data.campaigns || []);
    } catch (error) {
      console.error('Error fetching campaigns:', error);
    } finally {
      setCampaignsLoading(false);
    }
  };

  const createEmailCampaign = async () => {
    if (!newCampaign.campaign_name || !newCampaign.subject || !newCampaign.body) {
      alert('Please fill in all required fields');
      return;
    }
    setSavingCampaign(true);
    try {
      await axios.post(`${API}/affiliate/campaigns/create`, {
        affiliate_code: affiliateCode,
        ...newCampaign
      });
      await fetchEmailCampaigns();
      setShowNewCampaign(false);
      setNewCampaign({
        campaign_name: '',
        subject: '',
        body: '',
        delay_days: 0,
        is_active: true
      });
      alert('Campaign created successfully!');
    } catch (error) {
      console.error('Error creating campaign:', error);
      alert(error.response?.data?.detail || 'Failed to create campaign');
    } finally {
      setSavingCampaign(false);
    }
  };

  const toggleCampaign = async (campaignId) => {
    try {
      await axios.put(`${API}/affiliate/campaigns/${campaignId}/toggle?affiliate_code=${affiliateCode}`);
      await fetchEmailCampaigns();
    } catch (error) {
      console.error('Error toggling campaign:', error);
      alert(error.response?.data?.detail || 'Failed to toggle campaign');
    }
  };

  const deleteCampaign = async (campaignId) => {
    if (!window.confirm('Are you sure you want to delete this campaign?')) return;
    try {
      await axios.delete(`${API}/affiliate/campaigns/${campaignId}?affiliate_code=${affiliateCode}`);
      await fetchEmailCampaigns();
    } catch (error) {
      console.error('Error deleting campaign:', error);
      alert(error.response?.data?.detail || 'Failed to delete campaign');
    }
  };

  const fetchEmailTemplates = async () => {
    try {
      const response = await axios.get(`${API}/affiliate/email-templates`);
      setEmailTemplates(response.data.templates || []);
    } catch (error) {
      console.error('Error fetching email templates:', error);
    }
  };

  const activateTemplate = async (templateId) => {
    setActivatingTemplate(templateId);
    try {
      await axios.post(`${API}/affiliate/campaigns/create-from-template?affiliate_code=${affiliateCode}&template_id=${templateId}`);
      await fetchEmailCampaigns();
      alert('Template activated! Campaign is now live.');
    } catch (error) {
      console.error('Error activating template:', error);
      alert(error.response?.data?.detail || 'Failed to activate template');
    } finally {
      setActivatingTemplate(null);
    }
  };

  const fetchSourceAnalytics = async () => {
    if (!affiliateCode) return;
    setSourceAnalyticsLoading(true);
    try {
      const response = await axios.get(`${API}/affiliate/source-analytics/${affiliateCode}`);
      if (response.data.success) {
        setSourceAnalytics(response.data);
      }
    } catch (error) {
      console.error('Error fetching source analytics:', error);
    } finally {
      setSourceAnalyticsLoading(false);
    }
  };

  const handleW9Upload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    const formData = new FormData();
    formData.append('file', file);
    
    setUploadingW9(true);
    try {
      await axios.post(`${API}/affiliate/${affiliateCode}/upload-w9`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      await fetchVerificationStatus();
      alert('W-9 uploaded successfully!');
    } catch (error) {
      console.error('Error uploading W-9:', error);
      alert(error.response?.data?.detail || 'Failed to upload W-9');
    } finally {
      setUploadingW9(false);
    }
  };

  const handleIdUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    const formData = new FormData();
    formData.append('file', file);
    
    setUploadingId(true);
    try {
      await axios.post(`${API}/affiliate/${affiliateCode}/upload-id`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      await fetchVerificationStatus();
      alert('ID document uploaded successfully!');
    } catch (error) {
      console.error('Error uploading ID:', error);
      alert(error.response?.data?.detail || 'Failed to upload ID');
    } finally {
      setUploadingId(false);
    }
  };

  const handleSavePaymentInfo = async () => {
    if (!paymentForm.paypalEmail.trim() && paymentForm.preferredMethod === 'paypal') {
      alert('Please enter your PayPal email address');
      return;
    }
    
    setSavingPayment(true);
    try {
      await axios.post(`${API}/affiliate/${affiliateCode}/payment-info`, {
        paypal_info: paymentForm.paypalEmail,
        preferred_method: paymentForm.preferredMethod
      });
      await fetchVerificationStatus();
      alert('Payment information saved successfully!');
    } catch (error) {
      console.error('Error saving payment info:', error);
      alert(error.response?.data?.detail || 'Failed to save payment info');
    } finally {
      setSavingPayment(false);
    }
  };

  const fetchTemplateAnalytics = async () => {
    if (!affiliateCode) return;
    setAnalyticsLoading(true);
    try {
      const response = await axios.get(`${API}/affiliate/template-analytics/${affiliateCode}`);
      setTemplateAnalytics(response.data);
    } catch (error) {
      console.error('Error fetching template analytics:', error);
    } finally {
      setAnalyticsLoading(false);
    }
  };

  const fetchOgAbAnalytics = async (days = analyticsPeriod) => {
    if (!affiliateCode) return;
    setOgAbLoading(true);
    try {
      const response = await axios.get(`${API}/og/analytics/${affiliateCode}?days=${days}`);
      if (response.data.success) {
        setOgAbAnalytics(response.data);
      }
    } catch (error) {
      console.error('Error fetching OG A/B analytics:', error);
    } finally {
      setOgAbLoading(false);
    }
  };

  const fetchDetailedAnalytics = async (days = analyticsPeriod) => {
    if (!affiliateCode) return;
    setDetailedAnalyticsLoading(true);
    try {
      const response = await axios.get(`${API}/analytics/affiliate/${affiliateCode}?days=${days}`);
      if (response.data.success) {
        setDetailedAnalytics(response.data);
      }
    } catch (error) {
      console.error('Error fetching detailed analytics:', error);
    } finally {
      setDetailedAnalyticsLoading(false);
    }
  };

  const fetchCustomTemplates = async () => {
    if (!affiliateCode) return;
    try {
      const response = await axios.get(`${API}/affiliate/custom-templates/${affiliateCode}`);
      if (response.data.success) {
        setCustomTemplates(response.data.templates || []);
      }
    } catch (error) {
      console.error('Error fetching custom templates:', error);
    }
  };

  const generateCustomTemplate = async () => {
    if (!customTemplateConfig.name.trim() || !customTemplateConfig.headline.trim()) {
      return;
    }
    
    setGeneratingTemplate(true);
    try {
      const response = await axios.post(`${API}/affiliate/generate-template`, {
        affiliate_code: affiliateCode,
        config: customTemplateConfig
      });
      
      if (response.data.success) {
        setGeneratedTemplatePreview(response.data.template);
      }
    } catch (error) {
      console.error('Error generating template:', error);
    } finally {
      setGeneratingTemplate(false);
    }
  };

  const saveCustomTemplate = async () => {
    if (!generatedTemplatePreview) return;
    
    setSavingTemplate(true);
    try {
      const response = await axios.post(`${API}/affiliate/save-template`, {
        affiliate_code: affiliateCode,
        template: generatedTemplatePreview,
        config: customTemplateConfig
      });
      
      if (response.data.success) {
        // Refresh custom templates list
        await fetchCustomTemplates();
        // Clear the preview
        setGeneratedTemplatePreview(null);
        setShowTemplateGenerator(false);
        // Reset form
        setCustomTemplateConfig({
          name: '',
          headline: '',
          subheadline: '',
          primaryColor: '#7c3aed',
          style: 'modern',
          tone: 'professional',
          keyBenefits: '',
          ctaText: 'Get Started Free'
        });
      }
    } catch (error) {
      console.error('Error saving template:', error);
    } finally {
      setSavingTemplate(false);
    }
  };

  const deleteCustomTemplate = async (templateId) => {
    if (!window.confirm('Are you sure you want to delete this template?')) return;
    
    try {
      await axios.delete(`${API}/affiliate/custom-template/${affiliateCode}/${templateId}`);
      await fetchCustomTemplates();
    } catch (error) {
      console.error('Error deleting template:', error);
    }
  };

  const copyLandingPageLink = (template) => {
    const baseUrl = 'https://upost.com';
    const url = template === 'default' 
      ? `${baseUrl}/p/${affiliateCode}`
      : `${baseUrl}/p/${affiliateCode}/${template}`;
    navigator.clipboard.writeText(url);
    setCopiedLandingPage(template);
    setTimeout(() => setCopiedLandingPage(null), 2000);
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError('');
    setLoginLoading(true);
    
    try {
      const response = await axios.post(`${API}/affiliate/login`, {
        email: loginEmail,
        password: loginPassword
      });
      
      if (response.data.affiliate_code) {
        setAffiliateCode(response.data.affiliate_code);
        setAffiliateToken(response.data.token);
        localStorage.setItem('affiliate_code', response.data.affiliate_code);
        localStorage.setItem('affiliate_token', response.data.token || '');
        localStorage.setItem('affiliate_name', response.data.name || '');
        localStorage.setItem('affiliate_email', response.data.email || '');
      }
    } catch (error) {
      console.error('Login error:', error);
      setLoginError(error.response?.data?.detail || 'Invalid email or password');
    } finally {
      setLoginLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('affiliate_code');
    localStorage.removeItem('affiliate_token');
    localStorage.removeItem('affiliate_name');
    localStorage.removeItem('affiliate_email');
    setAffiliateCode(null);
    setAffiliateToken(null);
    setStats(null);
  };

  const copyToClipboard = (text, type) => {
    navigator.clipboard.writeText(text);
    if (type === 'partner') {
      setCopiedPartner(true);
      setTimeout(() => setCopiedPartner(false), 2000);
    } else {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-slate-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  // Login form
  if (!affiliateCode || !stats) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full shadow-xl">
          <CardHeader className="text-center pb-2">
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Users className="h-8 w-8 text-blue-600" />
            </div>
            <CardTitle className="text-2xl">Affiliate Dashboard</CardTitle>
            <CardDescription>Sign in to access your partner dashboard</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              {loginError && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm flex items-center gap-2">
                  <AlertCircle className="h-4 w-4" />
                  {loginError}
                </div>
              )}
              
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1 block">Email</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    type="email"
                    placeholder="your@email.com"
                    value={loginEmail}
                    onChange={(e) => setLoginEmail(e.target.value)}
                    className="pl-10"
                    required
                    data-testid="affiliate-login-email"
                  />
                </div>
              </div>
              
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1 block">Password</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    type={showPassword ? "text" : "password"}
                    placeholder="••••••••"
                    value={loginPassword}
                    onChange={(e) => setLoginPassword(e.target.value)}
                    className="pl-10 pr-10"
                    required
                    data-testid="affiliate-login-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    tabIndex={-1}
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              
              <Button 
                type="submit" 
                className="w-full bg-blue-600 hover:bg-blue-700"
                disabled={loginLoading}
                data-testid="affiliate-login-submit"
              >
                {loginLoading ? (
                  <>
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    Signing in...
                  </>
                ) : (
                  <>
                    <LogIn className="h-4 w-4 mr-2" />
                    Sign In
                  </>
                )}
              </Button>
              
              {/* Forgot Password Link */}
              <div className="mt-4 text-center">
                <Link 
                  to="/affiliate/password-reset" 
                  className="text-sm text-blue-600 hover:text-blue-700 hover:underline"
                  data-testid="forgot-password-link"
                >
                  Forgot your password?
                </Link>
              </div>
            </form>
            
            <div className="mt-6 pt-6 border-t text-center">
              <p className="text-sm text-slate-500 mb-3">Don't have an account?</p>
              <Link to="/affiliate">
                <Button variant="outline" className="w-full">
                  <UserPlus className="h-4 w-4 mr-2" />
                  Join Affiliate Program
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const currentTier = stats.current_tier || { name: 'Starter', rate: 0.10, emoji: '🌱' };
  const nextTier = stats.next_tier;
  const totalConversions = stats.stats?.total_conversions || 0;
  const teamEarnings = stats.stats?.team_earnings || 0;
  const recurringEarnings = stats.stats?.recurring_earnings || 0;
  const teamMembers = stats.team_members || [];

  return (
    <div className="min-h-screen bg-slate-50">
      
      {/* Header with Navigation */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
                <Users className="h-5 w-5 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-slate-900">Partner Portal</h1>
                <p className="text-sm text-slate-500">Welcome, {stats.name}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Link to="/">
                <Button variant="ghost" size="sm">
                  <Home className="h-4 w-4 mr-2" />
                  Home
                </Button>
              </Link>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={handleLogout}
                className="text-red-600 border-red-200 hover:bg-red-50"
                data-testid="affiliate-logout-btn"
              >
                <LogOut className="h-4 w-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Main Tabs Navigation */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-white border border-slate-200 p-1 h-auto flex-wrap">
            <TabsTrigger value="dashboard" className="flex items-center gap-2 data-[state=active]:bg-blue-600 data-[state=active]:text-white">
              <LayoutDashboard className="h-4 w-4" />
              Dashboard
            </TabsTrigger>
            <TabsTrigger value="leads" className="flex items-center gap-2 data-[state=active]:bg-blue-600 data-[state=active]:text-white">
              <Users className="h-4 w-4" />
              My Leads
              {leadsStats?.new > 0 && (
                <Badge className="ml-1 bg-blue-500 text-white text-xs">{leadsStats.new} new</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="landing-pages" className="flex items-center gap-2 data-[state=active]:bg-blue-600 data-[state=active]:text-white">
              <Globe className="h-4 w-4" />
              Landing Pages
            </TabsTrigger>
            <TabsTrigger value="refer-partners" className="flex items-center gap-2 data-[state=active]:bg-blue-600 data-[state=active]:text-white">
              <UserPlus className="h-4 w-4" />
              Refer Partners
              {teamMembers.length > 0 && (
                <Badge className="ml-1 bg-blue-500 text-white text-xs">{teamMembers.length}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="media-kit" className="flex items-center gap-2 data-[state=active]:bg-blue-600 data-[state=active]:text-white">
              <Image className="h-4 w-4" />
              Media Kit
            </TabsTrigger>
            <TabsTrigger value="payouts" className="flex items-center gap-2 data-[state=active]:bg-blue-600 data-[state=active]:text-white">
              <CreditCard className="h-4 w-4" />
              Payouts
            </TabsTrigger>
          </TabsList>

          {/* Dashboard Tab */}
          <TabsContent value="dashboard" className="space-y-6">
            {/* Current Tier Banner */}
            <Card className="bg-gradient-to-r from-blue-600 to-cyan-600 text-white overflow-hidden">
              <CardContent className="pt-6">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                  <div>
                    <p className="text-sm text-blue-100 mb-2">Current Tier</p>
                    <div className="flex items-center">
                      <span className="text-4xl mr-3">{currentTier.emoji}</span>
                      <div>
                        <p className="text-2xl font-bold">{currentTier.name}</p>
                        <p className="text-blue-100">{(currentTier.rate * 100).toFixed(0)}% Commission</p>
                      </div>
                    </div>
                  </div>
                  <div>
                    <p className="text-sm text-blue-100 mb-2">Total Referrals</p>
                    <p className="text-4xl font-bold">{totalConversions}</p>
                  </div>
                  <div>
                    <p className="text-sm text-blue-100 mb-2">Total Earnings</p>
                    <p className="text-4xl font-bold text-cyan-300">${stats.stats?.total_earned?.toFixed(2) || '0.00'}</p>
                  </div>
                  {nextTier && nextTier.tier && (
                    <div>
                      <p className="text-sm text-blue-100 mb-2">Next Tier</p>
                      <div className="flex items-center">
                        <span className="text-3xl mr-2">{nextTier.tier.emoji || '🎯'}</span>
                        <div>
                          <p className="text-xl font-bold">{nextTier.tier.name || 'Next'}</p>
                          <p className="text-sm text-blue-100">
                            {nextTier.referrals_needed || 0} more to unlock {((nextTier.tier.rate || 0) * 100).toFixed(0)}%
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Stats Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-500">Total Clicks</p>
                      <p className="text-3xl font-bold text-slate-900">{stats.stats?.total_clicks || 0}</p>
                    </div>
                    <MousePointer className="h-10 w-10 text-blue-500 opacity-80" />
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-500">Signups</p>
                      <p className="text-3xl font-bold text-slate-900">{stats.stats?.total_signups || 0}</p>
                      <p className="text-xs text-slate-400">{stats.stats?.conversion_rate || 0}% conversion</p>
                    </div>
                    <Users className="h-10 w-10 text-blue-500 opacity-80" />
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-500">Recurring Earnings</p>
                      <p className="text-3xl font-bold text-blue-600">${recurringEarnings.toFixed(2)}</p>
                      <p className="text-xs text-slate-400">From subscriptions</p>
                    </div>
                    <RefreshCw className="h-10 w-10 text-blue-500 opacity-80" />
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-gradient-to-br from-blue-50 to-slate-50 border-blue-200">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-blue-600 font-medium">Team Earnings</p>
                      <p className="text-3xl font-bold text-blue-700">${teamEarnings.toFixed(2)}</p>
                      <p className="text-xs text-blue-500">10% from {teamMembers.length} partner(s)</p>
                    </div>
                    <UserPlus className="h-10 w-10 text-blue-500 opacity-80" />
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Your Referral Link */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Share2 className="h-5 w-5 text-blue-600" />
                  Your Customer Referral Link
                </CardTitle>
                <CardDescription>Share this link to earn commissions when customers sign up</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex gap-2">
                  <Input 
                    value={stats.referral_link} 
                    readOnly 
                    className="font-mono text-sm bg-slate-50"
                  />
                  <Button 
                    onClick={() => copyToClipboard(stats.referral_link, 'customer')} 
                    variant={copied ? "default" : "outline"}
                    className={copied ? "bg-blue-600" : ""}
                  >
                    {copied ? <CheckCircle className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                  </Button>
                  <Button 
                    onClick={() => window.open(stats.referral_link, '_blank')}
                    variant="outline"
                  >
                    <ExternalLink className="h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Page-Specific Affiliate Links */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Globe className="h-5 w-5 text-blue-600" />
                  Page-Specific Referral Links
                </CardTitle>
                <CardDescription>Share targeted links to specific service pages for higher conversions</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {[
                    { path: '/gift-portfolio', label: 'Gift a Portfolio', desc: 'Graduation gift — prepaid portfolios' },
                    { path: '/personal-portfolio', label: 'Personal Portfolio', desc: 'Portfolio websites' },
                    { path: '/ppc-audit', label: 'Free PPC Audit', desc: 'PPC/SEM health check' },
                    { path: '/social-media-pricing', label: 'Social Media Management', desc: 'Social media plans' },
                    { path: '/social-media-pricing?service=instagram', label: 'Instagram Marketing', desc: 'Instagram growth & management' },
                    { path: '/local-business', label: 'Local Business', desc: 'Local business websites' },
                    { path: '/dealers', label: 'Dealers', desc: 'Dealer websites' },
                    { path: '/mobile-apps', label: 'Mobile Apps', desc: 'Mobile app development' },
                    { path: '/enterprise', label: 'Enterprise', desc: 'Enterprise solutions' },
                    { path: '/ai-visibility-demo', label: 'AI Visibility', desc: 'AI search monitoring' },
                    { path: '/seo-turbo', label: 'SEO Turbo', desc: 'SEO packages' },
                  ].map(({ path, label, desc }) => {
                    const url = `https://upost.com${path}?ref=${affiliateCode}`;
                    return (
                      <div key={path} className="flex items-center gap-2 bg-slate-50 rounded-lg p-2">
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-slate-800">{label}</p>
                          <p className="text-xs text-slate-500 truncate font-mono">{url}</p>
                        </div>
                        <Button
                          size="sm"
                          variant="outline"
                          className="shrink-0 h-8"
                          data-testid={`copy-link-${path.replace(/\//g, '')}`}
                          onClick={() => {
                            navigator.clipboard.writeText(url);
                            copyToClipboard(url, `page-${path}`);
                          }}
                        >
                          <Copy className="h-3 w-3 mr-1" /> Copy
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="shrink-0 h-8 w-8 p-0"
                          onClick={() => window.open(url, '_blank')}
                        >
                          <ExternalLink className="h-3 w-3" />
                        </Button>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>

            {/* Recent Referrals */}
            <Card>
              <CardHeader>
                <CardTitle>Recent Referrals</CardTitle>
                <CardDescription>Your last 10 referrals</CardDescription>
              </CardHeader>
              <CardContent>
                {stats.referrals && stats.referrals.length > 0 ? (
                  <div className="space-y-3">
                    {stats.referrals.map((referral, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                        <div className="flex items-center">
                          <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center mr-3">
                            <Users className="h-5 w-5 text-blue-600" />
                          </div>
                          <div>
                            <p className="font-medium text-slate-900">{referral.referred_email}</p>
                            <p className="text-xs text-slate-500">
                              {new Date(referral.referred_at).toLocaleDateString()}
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <Badge className={
                            referral.status === 'converted' ? 'bg-blue-600' :
                            referral.status === 'signed_up' ? 'bg-blue-600' :
                            'bg-gray-400'
                          }>
                            {referral.status === 'converted' ? 'Converted' :
                             referral.status === 'signed_up' ? 'Signed Up' :
                             'Pending'}
                          </Badge>
                          {referral.commission_earned > 0 && (
                            <p className="text-sm font-semibold text-blue-600 mt-1">
                              +${referral.commission_earned.toFixed(2)}
                            </p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <Users className="h-16 w-16 text-slate-300 mx-auto mb-4" />
                    <p className="text-slate-600 mb-4">No referrals yet. Start sharing your link!</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Leads Tab */}
          <TabsContent value="leads" className="space-y-6">
            {/* Leads Header */}
            <Card className="bg-gradient-to-r from-slate-800 to-slate-900 text-white">
              <CardContent className="pt-6 pb-6">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                  <div>
                    <h2 className="text-2xl font-bold mb-2">📋 My Leads</h2>
                    <p className="text-slate-300">
                      People who submitted forms on your landing page. We'll contact them and you get paid when they convert!
                    </p>
                  </div>
                  <Button 
                    onClick={fetchLeads} 
                    variant="outline" 
                    className="border-white/30 text-white hover:bg-white/10"
                  >
                    <RefreshCw className={`h-4 w-4 mr-2 ${leadsLoading ? 'animate-spin' : ''}`} />
                    Refresh
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Leads Stats */}
            {leadsStats && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card className="bg-white">
                  <CardContent className="pt-4 pb-4">
                    <div className="flex items-center gap-3">
                      <div className="bg-blue-100 p-2 rounded-lg">
                        <Users className="h-5 w-5 text-blue-600" />
                      </div>
                      <div>
                        <p className="text-2xl font-bold text-slate-900">{leadsStats.total}</p>
                        <p className="text-xs text-slate-500">Total Leads</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card className="bg-white">
                  <CardContent className="pt-4 pb-4">
                    <div className="flex items-center gap-3">
                      <div className="bg-blue-100 p-2 rounded-lg">
                        <AlertCircle className="h-5 w-5 text-blue-600" />
                      </div>
                      <div>
                        <p className="text-2xl font-bold text-blue-600">{leadsStats.new}</p>
                        <p className="text-xs text-slate-500">New Leads</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card className="bg-white">
                  <CardContent className="pt-4 pb-4">
                    <div className="flex items-center gap-3">
                      <div className="bg-blue-100 p-2 rounded-lg">
                        <CheckCircle className="h-5 w-5 text-blue-600" />
                      </div>
                      <div>
                        <p className="text-2xl font-bold text-blue-600">{leadsStats.converted}</p>
                        <p className="text-xs text-slate-500">Converted</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card className="bg-white">
                  <CardContent className="pt-4 pb-4">
                    <div className="flex items-center gap-3">
                      <div className="bg-blue-100 p-2 rounded-lg">
                        <TrendingUp className="h-5 w-5 text-blue-600" />
                      </div>
                      <div>
                        <p className="text-2xl font-bold text-blue-600">{leadsStats.conversion_rate}%</p>
                        <p className="text-xs text-slate-500">Conversion Rate</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* How It Works Info Box */}
            <Card className="bg-blue-50 border-blue-200">
              <CardContent className="pt-4 pb-4">
                <div className="flex items-start gap-3">
                  <div className="bg-blue-100 p-2 rounded-full">
                    <Sparkles className="h-5 w-5 text-blue-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-blue-900 mb-1">How Leads Work</h4>
                    <ol className="text-sm text-blue-800 space-y-1 list-decimal list-inside">
                      <li><strong>Someone fills out your landing page form</strong> → You see them here instantly</li>
                      <li><strong>Our team reaches out to them</strong> → We handle the sales process</li>
                      <li><strong>They become a customer</strong> → You earn your commission! 💰</li>
                    </ol>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Filter Buttons */}
            <div className="flex gap-2 flex-wrap">
              <Button
                variant={leadsFilter === 'all' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setLeadsFilter('all')}
                className={leadsFilter === 'all' ? 'bg-slate-900' : ''}
              >
                All ({leads.length})
              </Button>
              <Button
                variant={leadsFilter === 'new' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setLeadsFilter('new')}
                className={leadsFilter === 'new' ? 'bg-blue-600' : ''}
              >
                New ({leads.filter(l => l.status === 'new').length})
              </Button>
              <Button
                variant={leadsFilter === 'contacted' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setLeadsFilter('contacted')}
                className={leadsFilter === 'contacted' ? 'bg-blue-600' : ''}
              >
                Contacted ({leads.filter(l => l.status === 'contacted').length})
              </Button>
              <Button
                variant={leadsFilter === 'converted' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setLeadsFilter('converted')}
                className={leadsFilter === 'converted' ? 'bg-blue-600' : ''}
              >
                Converted ({leads.filter(l => l.status === 'converted').length})
              </Button>
            </div>

            {/* Leads List */}
            <Card>
              <CardContent className="p-0">
                {leadsLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
                    <span className="ml-3 text-slate-600">Loading leads...</span>
                  </div>
                ) : leads.length > 0 ? (
                  <div className="divide-y divide-slate-100">
                    {leads
                      .filter(lead => leadsFilter === 'all' || lead.status === leadsFilter)
                      .map((lead) => (
                        <div key={lead.lead_id} className="p-4 hover:bg-slate-50 transition-colors">
                          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <h4 className="font-semibold text-slate-900">{lead.name}</h4>
                                <Badge className={`text-xs ${
                                  lead.status === 'new' ? 'bg-blue-100 text-blue-700' :
                                  lead.status === 'contacted' ? 'bg-blue-100 text-blue-700' :
                                  lead.status === 'qualified' ? 'bg-slate-100 text-slate-700' :
                                  lead.status === 'converted' ? 'bg-blue-100 text-blue-700' :
                                  'bg-slate-100 text-slate-700'
                                }`}>
                                  {lead.status === 'new' && '🆕'} {lead.status.charAt(0).toUpperCase() + lead.status.slice(1)}
                                </Badge>
                              </div>
                              <p className="text-sm text-slate-600 font-medium">{lead.business_name}</p>
                              <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-sm text-slate-500">
                                <span className="flex items-center gap-1">
                                  <Mail className="h-3 w-3" /> {lead.email}
                                </span>
                                {lead.phone && (
                                  <span className="flex items-center gap-1">
                                    📞 {lead.phone}
                                  </span>
                                )}
                                {lead.service_interest && (
                                  <span className="flex items-center gap-1">
                                    🎯 {lead.service_interest}
                                  </span>
                                )}
                              </div>
                            </div>
                            <div className="text-right">
                              <p className="text-xs text-slate-400">
                                {new Date(lead.created_at).toLocaleDateString('en-US', {
                                  month: 'short',
                                  day: 'numeric',
                                  year: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })}
                              </p>
                              {lead.status === 'converted' && (
                                <Badge className="mt-1 bg-blue-500 text-white">
                                  💰 Commission Earned!
                                </Badge>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                  </div>
                ) : (
                  <div className="text-center py-16">
                    <div className="bg-slate-100 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Users className="h-10 w-10 text-slate-400" />
                    </div>
                    <h3 className="text-lg font-semibold text-slate-700 mb-2">No leads yet</h3>
                    <p className="text-slate-500 mb-6 max-w-md mx-auto">
                      When someone fills out the form on your landing page, they'll appear here. 
                      Start sharing your link to get leads!
                    </p>
                    <div className="flex flex-col sm:flex-row gap-3 justify-center">
                      <Button
                        onClick={() => setActiveTab('landing-pages')}
                        className="bg-blue-600 hover:bg-blue-700"
                      >
                        <Globe className="h-4 w-4 mr-2" />
                        Create Landing Page
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => {
                          const link = `https://upost.com/?ref=${affiliateCode}`;
                          navigator.clipboard.writeText(link);
                          setCopied(true);
                          setTimeout(() => setCopied(false), 2000);
                        }}
                      >
                        {copied ? <CheckCircle className="h-4 w-4 mr-2" /> : <Copy className="h-4 w-4 mr-2" />}
                        {copied ? 'Copied!' : 'Copy Referral Link'}
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Email CRM - Automated Follow-ups */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <Mail className="h-5 w-5 text-blue-600" />
                      Email CRM
                      <Badge className="bg-blue-100 text-blue-700">Automated</Badge>
                    </CardTitle>
                    <CardDescription>
                      Set up automated email campaigns to nurture your leads
                    </CardDescription>
                  </div>
                  <Button
                    onClick={() => setShowNewCampaign(!showNewCampaign)}
                    className={showNewCampaign ? 'bg-slate-600' : 'bg-blue-600 hover:bg-blue-700'}
                  >
                    {showNewCampaign ? 'Cancel' : '+ New Campaign'}
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {/* New Campaign Form */}
                {showNewCampaign && (
                  <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
                    <h4 className="font-medium text-slate-900 mb-4">Create New Campaign</h4>
                    <div className="grid gap-4">
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">Campaign Name *</label>
                        <Input
                          placeholder="e.g., Welcome Email, Follow-up Sequence"
                          value={newCampaign.campaign_name}
                          onChange={(e) => setNewCampaign({...newCampaign, campaign_name: e.target.value})}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">Subject Line *</label>
                        <Input
                          placeholder="e.g., Thanks for your interest in AI Visibility!"
                          value={newCampaign.subject}
                          onChange={(e) => setNewCampaign({...newCampaign, subject: e.target.value})}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">Email Body *</label>
                        <Textarea
                          placeholder="Write your email content here. Use {name} to personalize with lead's name..."
                          value={newCampaign.body}
                          onChange={(e) => setNewCampaign({...newCampaign, body: e.target.value})}
                          rows={5}
                        />
                        <p className="text-xs text-slate-500 mt-1">Tip: Use {'{name}'} and {'{business}'} for personalization</p>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-1">Send After (Days)</label>
                          <Input
                            type="number"
                            min="0"
                            placeholder="0"
                            value={newCampaign.delay_days}
                            onChange={(e) => setNewCampaign({...newCampaign, delay_days: parseInt(e.target.value) || 0})}
                          />
                          <p className="text-xs text-slate-500 mt-1">0 = send immediately</p>
                        </div>
                        <div className="flex items-end">
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={newCampaign.is_active}
                              onChange={(e) => setNewCampaign({...newCampaign, is_active: e.target.checked})}
                              className="w-4 h-4 rounded border-slate-300"
                            />
                            <span className="text-sm text-slate-700">Active immediately</span>
                          </label>
                        </div>
                      </div>
                      <Button
                        onClick={createEmailCampaign}
                        disabled={savingCampaign || !newCampaign.campaign_name || !newCampaign.subject || !newCampaign.body}
                        className="bg-blue-600 hover:bg-blue-700 w-fit"
                      >
                        {savingCampaign ? (
                          <Loader2 className="h-4 w-4 animate-spin mr-2" />
                        ) : (
                          <Save className="h-4 w-4 mr-2" />
                        )}
                        Create Campaign
                      </Button>
                    </div>
                  </div>
                )}

                {/* Existing Campaigns */}
                {campaignsLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
                    <span className="ml-2 text-slate-500">Loading campaigns...</span>
                  </div>
                ) : emailCampaigns.length === 0 ? (
                  <div className="text-center py-8 bg-slate-50 rounded-lg border-2 border-dashed border-slate-200">
                    <Mail className="h-12 w-12 mx-auto text-slate-300 mb-3" />
                    <h3 className="text-lg font-medium text-slate-700 mb-1">No Email Campaigns Yet</h3>
                    <p className="text-sm text-slate-500 mb-4">
                      Create automated email campaigns to follow up with your leads
                    </p>
                    {!showNewCampaign && (
                      <Button
                        onClick={() => setShowNewCampaign(true)}
                        className="bg-blue-600 hover:bg-blue-700"
                      >
                        Create Your First Campaign
                      </Button>
                    )}
                  </div>
                ) : (
                  <div className="space-y-3">
                    {emailCampaigns.map((campaign) => (
                      <div
                        key={campaign.campaign_id}
                        className={`border rounded-lg p-4 ${campaign.is_active ? 'border-blue-200 bg-blue-50' : 'border-slate-200 bg-slate-50'}`}
                      >
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <h4 className="font-medium text-slate-900">{campaign.campaign_name}</h4>
                              <Badge className={campaign.is_active ? 'bg-blue-100 text-blue-700' : 'bg-slate-200 text-slate-600'}>
                                {campaign.is_active ? 'Active' : 'Paused'}
                              </Badge>
                            </div>
                            <p className="text-sm text-slate-600 mb-2">
                              <strong>Subject:</strong> {campaign.subject}
                            </p>
                            <p className="text-xs text-slate-500">
                              Sends {campaign.delay_days === 0 ? 'immediately' : `${campaign.delay_days} days after signup`} • {campaign.emails_sent || 0} emails sent
                            </p>
                          </div>
                          <div className="flex items-center gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => toggleCampaign(campaign.campaign_id)}
                            >
                              {campaign.is_active ? 'Pause' : 'Activate'}
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => deleteCampaign(campaign.campaign_id)}
                              className="text-red-600 hover:bg-red-50 border-red-200"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Ready-to-Use Templates */}
                {emailTemplates.length > 0 && (
                  <div className="mt-6">
                    <h4 className="font-medium text-slate-800 mb-3 flex items-center gap-2">
                      <Sparkles className="h-4 w-4 text-blue-600" />
                      Ready-to-Use Templates
                      <Badge className="bg-blue-100 text-blue-700">One-Click Activate</Badge>
                    </h4>
                    <div className="grid gap-3">
                      {emailTemplates.map((template) => {
                        const isActive = emailCampaigns.some(c => c.from_template === template.template_id);
                        const categoryColors = {
                          welcome: 'from-blue-500 to-cyan-500',
                          followup: 'from-blue-500 to-blue-600',
                          value: 'from-blue-500 to-slate-600',
                          urgency: 'from-slate-600 to-blue-700',
                          reengagement: 'from-slate-500 to-blue-500'
                        };
                        
                        return (
                          <div
                            key={template.template_id}
                            className={`border rounded-lg p-4 ${isActive ? 'bg-blue-50 border-blue-200' : 'bg-white hover:border-blue-300'}`}
                          >
                            <div className="flex items-start justify-between gap-4">
                              <div className="flex items-start gap-3">
                                <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${categoryColors[template.category] || 'from-slate-500 to-slate-600'} flex items-center justify-center shrink-0`}>
                                  <Mail className="h-5 w-5 text-white" />
                                </div>
                                <div>
                                  <div className="flex items-center gap-2">
                                    <h5 className="font-medium text-slate-900">{template.name}</h5>
                                    <Badge className="bg-slate-100 text-slate-600 text-xs">
                                      Day {template.delay_days}
                                    </Badge>
                                    {isActive && (
                                      <Badge className="bg-blue-100 text-blue-700 text-xs">
                                        <CheckCircle className="h-3 w-3 mr-1" />
                                        Active
                                      </Badge>
                                    )}
                                  </div>
                                  <p className="text-sm text-slate-600">{template.description}</p>
                                  <p className="text-xs text-slate-400 mt-1 truncate max-w-md">
                                    Subject: {template.subject}
                                  </p>
                                </div>
                              </div>
                              <Button
                                onClick={() => activateTemplate(template.template_id)}
                                disabled={activatingTemplate === template.template_id || isActive}
                                className={isActive ? 'bg-blue-600' : 'bg-blue-600 hover:bg-blue-700'}
                                size="sm"
                              >
                                {activatingTemplate === template.template_id ? (
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                ) : isActive ? (
                                  <>
                                    <CheckCircle className="h-4 w-4 mr-1" />
                                    Activated
                                  </>
                                ) : (
                                  'Activate'
                                )}
                              </Button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* How Email CRM Works */}
                <div className="mt-6 p-4 bg-slate-50 rounded-lg">
                  <h4 className="font-medium text-slate-800 mb-2 flex items-center gap-2">
                    <Sparkles className="h-4 w-4 text-blue-600" />
                    How Email CRM Works
                  </h4>
                  <ul className="text-sm text-slate-600 space-y-1">
                    <li>• When a lead fills out your form, they automatically receive your active campaigns</li>
                    <li>• Set delay days to create a drip sequence (e.g., Day 0, Day 3, Day 7)</li>
                    <li>• Use {'{name}'} and {'{business}'} tags for personalization</li>
                    <li>• Track how many emails each campaign has sent</li>
                  </ul>
                </div>
              </CardContent>
            </Card>

            {/* Tips for Getting More Leads */}
            <Card className="bg-slate-50 border-slate-200">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Sparkles className="h-5 w-5 text-blue-600" />
                  Tips for Getting More Leads
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-3 gap-4">
                  <div className="bg-white p-4 rounded-lg border border-slate-200">
                    <h4 className="font-medium text-slate-900 mb-2">1. Create a Landing Page</h4>
                    <p className="text-sm text-slate-600">
                      Custom landing pages convert 2-3x better than generic links. Pick your industry template!
                    </p>
                  </div>
                  <div className="bg-white p-4 rounded-lg border border-slate-200">
                    <h4 className="font-medium text-slate-900 mb-2">2. Use AI Marketing Tools</h4>
                    <p className="text-sm text-slate-600">
                      Generate emails, social posts, and video scripts tailored to your audience.
                    </p>
                  </div>
                  <div className="bg-white p-4 rounded-lg border border-slate-200">
                    <h4 className="font-medium text-slate-900 mb-2">3. Share Consistently</h4>
                    <p className="text-sm text-slate-600">
                      Post about AI visibility 2-3x per week. Consistency beats one-time blasts!
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Landing Pages Tab */}
          <TabsContent value="landing-pages" className="space-y-6">
            {/* Hero Banner */}
            <Card className="bg-gradient-to-r from-blue-600 via-blue-700 to-cyan-600 text-white">
              <CardContent className="pt-6 pb-6">
                <div className="text-center max-w-2xl mx-auto">
                  <Globe className="h-12 w-12 mx-auto mb-4 opacity-90" />
                  <h2 className="text-2xl font-bold mb-2">Your Hosted Landing Pages</h2>
                  <p className="text-blue-100 mb-4">
                    Professional, high-converting landing pages hosted on our domain. 
                    Just share the link — we handle everything else!
                  </p>
                  <div className="bg-white/20 rounded-lg p-4 inline-block">
                    <p className="text-sm text-blue-100">
                      All clicks and conversions are automatically tracked. See which templates perform best!
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* 📊 COMPREHENSIVE ANALYTICS DASHBOARD */}
            <Card className="border-2 border-blue-200">
              <CardHeader className="bg-gradient-to-r from-blue-50 to-slate-50 border-b border-blue-100">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <div>
                    <CardTitle className="flex items-center gap-2 text-blue-900">
                      <BarChart3 className="h-5 w-5 text-blue-600" />
                      📊 Landing Page Analytics
                    </CardTitle>
                    <CardDescription className="text-blue-700">
                      Track views, clicks, bounce rate, conversions, and visitor sources
                    </CardDescription>
                  </div>
                  <div className="flex items-center gap-2">
                    <select
                      value={analyticsPeriod}
                      onChange={(e) => {
                        setAnalyticsPeriod(parseInt(e.target.value));
                        fetchDetailedAnalytics(parseInt(e.target.value));
                      }}
                      className="text-sm border border-blue-200 rounded-lg px-3 py-2 bg-white text-slate-700"
                    >
                      <option value={7}>Last 7 days</option>
                      <option value={30}>Last 30 days</option>
                      <option value={90}>Last 90 days</option>
                    </select>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => fetchDetailedAnalytics(analyticsPeriod)}
                      className="border-blue-200 text-blue-700 hover:bg-blue-50"
                    >
                      <RefreshCw className={`h-4 w-4 ${detailedAnalyticsLoading ? 'animate-spin' : ''}`} />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="pt-6">
                {detailedAnalyticsLoading && !detailedAnalytics ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
                    <span className="ml-3 text-slate-500">Loading analytics...</span>
                  </div>
                ) : detailedAnalytics?.summary ? (
                  <div className="space-y-6">
                    {/* Key Metrics Grid */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-4 border border-blue-200">
                        <div className="flex items-center gap-2 mb-2">
                          <Eye className="h-5 w-5 text-blue-600" />
                          <span className="text-sm font-medium text-blue-700">Page Views</span>
                        </div>
                        <p className="text-3xl font-bold text-blue-900">
                          {detailedAnalytics.summary.total_page_views?.toLocaleString() || 0}
                        </p>
                        <p className="text-xs text-blue-600 mt-1">
                          {detailedAnalytics.summary.unique_visitors?.toLocaleString() || 0} unique visitors
                        </p>
                      </div>
                      
                      <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-4 border border-blue-200">
                        <div className="flex items-center gap-2 mb-2">
                          <MousePointer className="h-5 w-5 text-blue-600" />
                          <span className="text-sm font-medium text-blue-700">CTA Clicks</span>
                        </div>
                        <p className="text-3xl font-bold text-blue-900">
                          {detailedAnalytics.summary.cta_clicks?.toLocaleString() || 0}
                        </p>
                        <p className="text-xs text-blue-600 mt-1">
                          {detailedAnalytics.summary.click_through_rate || 0}% CTR
                        </p>
                      </div>
                      
                      <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-4 border border-blue-200">
                        <div className="flex items-center gap-2 mb-2">
                          <Users className="h-5 w-5 text-blue-600" />
                          <span className="text-sm font-medium text-blue-700">Leads</span>
                        </div>
                        <p className="text-3xl font-bold text-blue-900">
                          {detailedAnalytics.summary.leads_generated?.toLocaleString() || 0}
                        </p>
                        <p className="text-xs text-blue-600 mt-1">
                          {detailedAnalytics.summary.conversion_rate || 0}% conversion
                        </p>
                      </div>
                      
                      <div className="bg-gradient-to-br from-slate-50 to-slate-100 rounded-xl p-4 border border-slate-200">
                        <div className="flex items-center gap-2 mb-2">
                          <TrendingUp className="h-5 w-5 text-slate-600" />
                          <span className="text-sm font-medium text-slate-700">Bounce Rate</span>
                        </div>
                        <p className={`text-3xl font-bold ${
                          (detailedAnalytics.summary.bounce_rate || 0) > 70 ? 'text-slate-800' :
                          (detailedAnalytics.summary.bounce_rate || 0) > 50 ? 'text-slate-700' :
                          'text-blue-600'
                        }`}>
                          {detailedAnalytics.summary.bounce_rate || 0}%
                        </p>
                        <p className="text-xs text-slate-600 mt-1">
                          {(detailedAnalytics.summary.bounce_rate || 0) <= 50 ? '✅ Good' : 
                           (detailedAnalytics.summary.bounce_rate || 0) <= 70 ? '⚠️ Average' : '❌ High'}
                        </p>
                      </div>
                    </div>
                    
                    {/* Conversion Funnel */}
                    {detailedAnalytics.funnel && (
                      <div className="bg-slate-50 rounded-xl p-5 border border-slate-200">
                        <h4 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                          <TrendingUp className="h-5 w-5 text-blue-600" />
                          Conversion Funnel
                        </h4>
                        <div className="space-y-3">
                          {[
                            { label: 'Page Views', value: detailedAnalytics.funnel.views || 0, color: 'bg-blue-500' },
                            { label: 'Engaged Visitors', value: detailedAnalytics.funnel.engaged || 0, color: 'bg-blue-400' },
                            { label: 'Clicked CTA', value: detailedAnalytics.funnel.clicked_cta || 0, color: 'bg-blue-500' },
                            { label: 'Form Submissions', value: detailedAnalytics.funnel.submitted_form || 0, color: 'bg-blue-500' },
                            { label: 'Became Lead', value: detailedAnalytics.funnel.became_lead || 0, color: 'bg-blue-600' }
                          ].map((step, idx) => {
                            const maxValue = detailedAnalytics.funnel.views || 1;
                            const percentage = Math.round((step.value / maxValue) * 100);
                            return (
                              <div key={idx} className="space-y-1">
                                <div className="flex justify-between text-sm">
                                  <span className="text-slate-700">{step.label}</span>
                                  <span className="font-medium text-slate-900">{step.value.toLocaleString()} ({percentage}%)</span>
                                </div>
                                <div className="h-3 bg-slate-200 rounded-full overflow-hidden">
                                  <div 
                                    className={`h-full ${step.color} rounded-full transition-all duration-700`}
                                    style={{ width: `${percentage}%` }}
                                  />
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                    
                    {/* Source & Device Breakdown */}
                    <div className="grid md:grid-cols-2 gap-4">
                      {/* Traffic Sources */}
                      {detailedAnalytics.breakdown?.by_source && Object.keys(detailedAnalytics.breakdown.by_source).length > 0 && (
                        <div className="bg-white rounded-xl p-5 border border-slate-200">
                          <h4 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                            <Globe className="h-5 w-5 text-blue-600" />
                            Traffic Sources
                          </h4>
                          <div className="space-y-3">
                            {Object.entries(detailedAnalytics.breakdown.by_source)
                              .sort((a, b) => b[1] - a[1])
                              .slice(0, 6)
                              .map(([source, count], idx) => {
                                const total = Object.values(detailedAnalytics.breakdown.by_source).reduce((a, b) => a + b, 0);
                                const percentage = Math.round((count / total) * 100);
                                const sourceIcons = {
                                  facebook: { icon: '📘', color: 'bg-blue-500' },
                                  instagram: { icon: '📷', color: 'bg-blue-500' },
                                  linkedin: { icon: '💼', color: 'bg-blue-700' },
                                  twitter: { icon: '🐦', color: 'bg-sky-500' },
                                  google: { icon: '🔍', color: 'bg-red-500' },
                                  direct: { icon: '🔗', color: 'bg-slate-500' },
                                  tiktok: { icon: '🎵', color: 'bg-black' }
                                };
                                const { icon, color } = sourceIcons[source.toLowerCase()] || { icon: '🌐', color: 'bg-gray-500' };
                                
                                return (
                                  <div key={idx} className="flex items-center gap-3">
                                    <span className="text-lg">{icon}</span>
                                    <div className="flex-1">
                                      <div className="flex justify-between text-sm mb-1">
                                        <span className="capitalize text-slate-700">{source}</span>
                                        <span className="font-medium text-slate-900">{count} ({percentage}%)</span>
                                      </div>
                                      <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                                        <div className={`h-full ${color} rounded-full`} style={{ width: `${percentage}%` }} />
                                      </div>
                                    </div>
                                  </div>
                                );
                              })}
                          </div>
                        </div>
                      )}
                      
                      {/* Device Breakdown */}
                      {detailedAnalytics.breakdown?.by_device && Object.keys(detailedAnalytics.breakdown.by_device).length > 0 && (
                        <div className="bg-white rounded-xl p-5 border border-slate-200">
                          <h4 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                            📱 Device Breakdown
                          </h4>
                          <div className="space-y-3">
                            {Object.entries(detailedAnalytics.breakdown.by_device)
                              .sort((a, b) => b[1] - a[1])
                              .map(([device, count], idx) => {
                                const total = Object.values(detailedAnalytics.breakdown.by_device).reduce((a, b) => a + b, 0);
                                const percentage = Math.round((count / total) * 100);
                                const deviceStyles = {
                                  desktop: { icon: '🖥️', color: 'bg-slate-600' },
                                  mobile: { icon: '📱', color: 'bg-blue-500' },
                                  tablet: { icon: '📟', color: 'bg-blue-500' }
                                };
                                const { icon, color } = deviceStyles[device.toLowerCase()] || { icon: '📱', color: 'bg-gray-500' };
                                
                                return (
                                  <div key={idx} className="flex items-center gap-3">
                                    <span className="text-2xl">{icon}</span>
                                    <div className="flex-1">
                                      <div className="flex justify-between text-sm mb-1">
                                        <span className="capitalize text-slate-700">{device}</span>
                                        <span className="font-medium text-slate-900">{count} ({percentage}%)</span>
                                      </div>
                                      <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                                        <div className={`h-full ${color} rounded-full`} style={{ width: `${percentage}%` }} />
                                      </div>
                                    </div>
                                  </div>
                                );
                              })}
                          </div>
                        </div>
                      )}
                    </div>
                    
                    {/* Daily Views Chart (Simple) */}
                    {detailedAnalytics.daily_views && detailedAnalytics.daily_views.length > 0 && (
                      <div className="bg-white rounded-xl p-5 border border-slate-200">
                        <h4 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                          📈 Daily Views Trend
                        </h4>
                        <div className="flex items-end gap-1 h-32">
                          {detailedAnalytics.daily_views.slice(-14).map((day, idx) => {
                            const maxViews = Math.max(...detailedAnalytics.daily_views.slice(-14).map(d => d.views));
                            const heightPercent = maxViews > 0 ? (day.views / maxViews) * 100 : 0;
                            return (
                              <div key={idx} className="flex-1 flex flex-col items-center group relative">
                                <div 
                                  className="w-full bg-gradient-to-t from-blue-500 to-blue-400 rounded-t transition-all hover:from-blue-600 hover:to-blue-500"
                                  style={{ height: `${Math.max(heightPercent, 4)}%` }}
                                />
                                <span className="text-xs text-slate-400 mt-1 truncate w-full text-center">
                                  {day.date.slice(5)}
                                </span>
                                {/* Tooltip */}
                                <div className="absolute bottom-full mb-2 hidden group-hover:block bg-slate-800 text-white text-xs rounded px-2 py-1 whitespace-nowrap z-10">
                                  {day.views} views on {day.date}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                    
                    {/* Template Performance by Source */}
                    {detailedAnalytics.breakdown?.by_template && Object.keys(detailedAnalytics.breakdown.by_template).length > 0 && (
                      <div className="bg-gradient-to-r from-blue-50 to-slate-50 rounded-xl p-5 border border-blue-200">
                        <h4 className="font-semibold text-blue-900 mb-4 flex items-center gap-2">
                          🎨 Views by Template
                        </h4>
                        <div className="grid gap-3">
                          {Object.entries(detailedAnalytics.breakdown.by_template)
                            .sort((a, b) => b[1] - a[1])
                            .map(([template, views], idx) => {
                              const total = Object.values(detailedAnalytics.breakdown.by_template).reduce((a, b) => a + b, 0);
                              const percentage = Math.round((views / total) * 100);
                              return (
                                <div key={idx} className="flex items-center justify-between bg-white rounded-lg p-3 border border-blue-100">
                                  <div className="flex items-center gap-2">
                                    <div className="w-3 h-3 rounded-full bg-gradient-to-r from-blue-500 to-blue-600" />
                                    <span className="font-medium text-slate-800 capitalize">{template.replace('-', ' ')}</span>
                                  </div>
                                  <div className="flex items-center gap-3">
                                    <span className="text-sm text-slate-600">{views} views</span>
                                    <Badge className="bg-blue-100 text-blue-700">{percentage}%</Badge>
                                  </div>
                                </div>
                              );
                            })}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <BarChart3 className="h-16 w-16 mx-auto text-slate-300 mb-4" />
                    <h3 className="text-lg font-semibold text-slate-700 mb-2">No Analytics Data Yet</h3>
                    <p className="text-slate-500 mb-4">
                      Share your landing page links to start tracking visitor engagement!
                    </p>
                    <p className="text-sm text-slate-400">
                      Data will appear here once visitors view your landing pages.
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Template Performance Analytics */}
            {templateAnalytics && templateAnalytics.template_stats && templateAnalytics.template_stats.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <BarChart3 className="h-5 w-5 text-blue-600" />
                    Template Click Performance
                  </CardTitle>
                  <CardDescription>
                    See which landing page templates are getting the most clicks
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {templateAnalytics.template_stats.map((stat, idx) => {
                      const maxClicks = Math.max(...templateAnalytics.template_stats.map(s => s.total_clicks));
                      const percentage = maxClicks > 0 ? (stat.total_clicks / maxClicks) * 100 : 0;
                      
                      return (
                        <div key={idx} className="space-y-1">
                          <div className="flex justify-between text-sm">
                            <span className="font-medium capitalize">{stat.template.replace('-', ' ')}</span>
                            <span className="text-slate-600">{stat.total_clicks} clicks</span>
                          </div>
                          <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                            <div 
                              className="h-full bg-gradient-to-r from-blue-500 to-blue-600 rounded-full transition-all duration-500"
                              style={{ width: `${percentage}%` }}
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* 🔬 OG Image A/B Testing Analytics */}
            {ogAbAnalytics && ogAbAnalytics.templates && ogAbAnalytics.templates.length > 0 && (
              <Card className="border-2 border-blue-200">
                <CardHeader className="bg-gradient-to-r from-blue-50 to-slate-50 border-b border-blue-100">
                  <CardTitle className="flex items-center gap-2 text-slate-900">
                    <Target className="h-5 w-5 text-blue-600" />
                    OG Image A/B Testing
                  </CardTitle>
                  <CardDescription className="text-slate-600">
                    Compare click-through rates across different social share images
                  </CardDescription>
                </CardHeader>
                <CardContent className="pt-6">
                  {/* Winner Recommendation */}
                  {ogAbAnalytics.winner && (
                    <div className="mb-6 p-4 bg-gradient-to-r from-blue-50 to-slate-50 rounded-xl border border-blue-200">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                          <Award className="h-6 w-6 text-blue-600" />
                        </div>
                        <div>
                          <p className="font-bold text-slate-900">Winning Template</p>
                          <p className="text-sm text-slate-700">{ogAbAnalytics.recommendation}</p>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {/* Template Performance Table */}
                  <div className="space-y-3">
                    {ogAbAnalytics.templates.map((template, idx) => {
                      const isWinner = ogAbAnalytics.winner?.template === template.template;
                      const maxCtr = Math.max(...ogAbAnalytics.templates.map(t => t.ctr || 0));
                      const barWidth = maxCtr > 0 ? ((template.ctr || 0) / maxCtr) * 100 : 0;
                      
                      return (
                        <div 
                          key={idx} 
                          className={`p-4 rounded-xl border ${isWinner ? 'bg-blue-50 border-blue-300' : 'bg-slate-50 border-slate-200'}`}
                        >
                          <div className="flex justify-between items-start mb-2">
                            <div>
                              <span className="font-medium capitalize flex items-center gap-2">
                                {template.template?.replace('-', ' ') || 'Unknown'}
                                {isWinner && <Badge className="bg-blue-100 text-blue-800 text-xs">Winner</Badge>}
                              </span>
                              <p className="text-xs text-slate-500 mt-1">
                                {template.page_type === 'recruit' ? '📣 Recruitment' : '🎯 Customer Promo'}
                              </p>
                            </div>
                            <div className="text-right">
                              <p className={`text-xl font-bold ${isWinner ? 'text-blue-600' : 'text-slate-700'}`}>
                                {(template.ctr || 0).toFixed(1)}%
                              </p>
                              <p className="text-xs text-slate-500">CTR</p>
                            </div>
                          </div>
                          
                          {/* CTR Progress Bar */}
                          <div className="h-2 bg-slate-200 rounded-full overflow-hidden mb-2">
                            <div 
                              className={`h-full rounded-full transition-all duration-500 ${isWinner ? 'bg-gradient-to-r from-blue-400 to-cyan-400' : 'bg-gradient-to-r from-slate-400 to-slate-500'}`}
                              style={{ width: `${barWidth}%` }}
                            />
                          </div>
                          
                          {/* Stats Row */}
                          <div className="flex justify-between text-xs text-slate-600">
                            <span>{template.total_impressions?.toLocaleString() || 0} impressions</span>
                            <span>{template.total_clicks?.toLocaleString() || 0} clicks</span>
                            <span>{template.days_active || 0} days tested</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  
                  {/* Note about statistical significance */}
                  {!ogAbAnalytics.winner && (
                    <div className="mt-4 p-3 bg-slate-50 rounded-lg border border-slate-200">
                      <p className="text-sm text-slate-700 flex items-center gap-2">
                        <AlertCircle className="h-4 w-4" />
                        Need 100+ impressions per variant for statistically significant results
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Available Templates */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Sparkles className="h-5 w-5 text-blue-600" />
                  Available Landing Page Templates
                </CardTitle>
                <CardDescription>
                  Choose a template, copy the link, and share it anywhere
                </CardDescription>
              </CardHeader>
              <CardContent>
                {/* General Templates */}
                <h4 className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-3">General Templates</h4>
                <div className="grid gap-4 mb-8">
                  {[
                    { 
                      id: 'default', 
                      name: 'AI Visibility (Default)', 
                      description: 'Focus on AI recommendations with stats and social proof',
                      color: 'from-blue-500 to-blue-600',
                      preview: 'Blue gradient, AI-focused messaging'
                    },
                    { 
                      id: 'sales-funnel', 
                      name: 'Sales Funnel', 
                      description: 'Problem-agitation-solution format for high conversions',
                      color: 'from-slate-700 to-slate-900',
                      preview: 'Dark theme, urgency-driven'
                    },
                    { 
                      id: 'case-study', 
                      name: 'Case Study', 
                      description: 'Results-focused with detailed success stories',
                      color: 'from-blue-600 to-cyan-500',
                      preview: 'Clean white design, testimonial-focused'
                    },
                    { 
                      id: 'comparison', 
                      name: 'Before/After', 
                      description: 'Visual transformation showcase side-by-side',
                      color: 'from-slate-600 to-blue-600',
                      preview: 'Split design, dramatic contrast'
                    },
                    { 
                      id: 'urgency', 
                      name: 'Urgency', 
                      description: 'Limited time offer style with countdown elements',
                      color: 'from-slate-900 to-blue-800',
                      preview: 'Black background, bold CTAs'
                    }
                  ].map((template) => {
                    const templateClicks = templateAnalytics?.summary?.template_breakdown?.[template.id] || 0;
                    const pageUrl = template.id === 'default' 
                      ? `https://upost.com/p/${affiliateCode}`
                      : `https://upost.com/p/${affiliateCode}/${template.id}`;
                    
                    return (
                      <div 
                        key={template.id}
                        className="border rounded-lg p-4 hover:border-blue-300 hover:shadow-md transition-all"
                      >
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                          <div className="flex items-start gap-4">
                            <div className={`w-12 h-12 rounded-lg bg-gradient-to-br ${template.color} flex items-center justify-center shrink-0`}>
                              <Globe className="h-6 w-6 text-white" />
                            </div>
                            <div>
                              <h3 className="font-semibold text-slate-900">{template.name}</h3>
                              <p className="text-sm text-slate-600">{template.description}</p>
                              <p className="text-xs text-slate-400 mt-1">{template.preview}</p>
                              {templateClicks > 0 && (
                                <Badge className="mt-2 bg-blue-100 text-blue-700">
                                  {templateClicks} clicks
                                </Badge>
                              )}
                            </div>
                          </div>
                          
                          <div className="flex gap-2 shrink-0">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => window.open(pageUrl, '_blank')}
                            >
                              <Eye className="h-4 w-4 mr-1" />
                              Preview
                            </Button>
                            <Button
                              size="sm"
                              onClick={() => {
                                navigator.clipboard.writeText(pageUrl);
                                setCopiedLandingPage(template.id);
                                setTimeout(() => setCopiedLandingPage(null), 2000);
                              }}
                              className={copiedLandingPage === template.id ? 'bg-blue-600' : ''}
                            >
                              {copiedLandingPage === template.id ? (
                                <><CheckCircle2 className="h-4 w-4 mr-1" /> Copied!</>
                              ) : (
                                <><Copy className="h-4 w-4 mr-1" /> Copy Link</>
                              )}
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => setShowSocialPosts(showSocialPosts === template.id ? null : template.id)}
                              className={showSocialPosts === template.id ? 'bg-blue-600 text-white' : ''}
                            >
                              {showSocialPosts === template.id ? (
                                <><Share2 className="h-4 w-4 mr-1" /> Hide Posts</>
                              ) : (
                                <><Share2 className="h-4 w-4 mr-1" /> Get Posts</>
                              )}
                            </Button>
                          </div>
                        </div>
                        
                        {/* Social Posts Panel for General Templates */}
                        {showSocialPosts === template.id && (
                          <div className="mt-4 bg-gradient-to-r from-slate-50 to-blue-50 rounded-xl p-4 border border-slate-200">
                            <h4 className="font-semibold text-slate-800 mb-3 flex items-center gap-2">
                              <Share2 className="h-4 w-4 text-blue-600" />
                              Ready-to-Post Content for {template.name}
                            </h4>
                            <div className="grid gap-4">
                              {/* Facebook */}
                              <div className="bg-white rounded-lg p-4 border border-blue-200">
                                <div className="flex items-center justify-between mb-2">
                                  <div className="flex items-center gap-2">
                                    <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center">
                                      <Facebook className="h-4 w-4 text-white" />
                                    </div>
                                    <span className="font-semibold text-blue-800">Facebook</span>
                                  </div>
                                  <Button
                                    size="sm"
                                    onClick={() => copyPostToClipboard(getSocialPosts(template.id, pageUrl).facebook, `${template.id}-fb`)}
                                    className={copiedPost === `${template.id}-fb` ? 'bg-blue-600' : 'bg-blue-600'}
                                  >
                                    {copiedPost === `${template.id}-fb` ? '✓ Copied!' : 'Copy Post'}
                                  </Button>
                                </div>
                                <pre className="text-xs text-slate-700 whitespace-pre-wrap bg-slate-50 p-3 rounded-lg max-h-32 overflow-y-auto">
                                  {getSocialPosts(template.id, pageUrl).facebook}
                                </pre>
                              </div>
                              
                              {/* LinkedIn */}
                              <div className="bg-white rounded-lg p-4 border border-blue-300">
                                <div className="flex items-center justify-between mb-2">
                                  <div className="flex items-center gap-2">
                                    <div className="w-8 h-8 bg-blue-700 rounded-full flex items-center justify-center">
                                      <Linkedin className="h-4 w-4 text-white" />
                                    </div>
                                    <span className="font-semibold text-blue-800">LinkedIn</span>
                                  </div>
                                  <Button
                                    size="sm"
                                    onClick={() => copyPostToClipboard(getSocialPosts(template.id, pageUrl).linkedin, `${template.id}-li`)}
                                    className={copiedPost === `${template.id}-li` ? 'bg-blue-600' : 'bg-blue-700'}
                                  >
                                    {copiedPost === `${template.id}-li` ? '✓ Copied!' : 'Copy Post'}
                                  </Button>
                                </div>
                                <pre className="text-xs text-slate-700 whitespace-pre-wrap bg-slate-50 p-3 rounded-lg max-h-32 overflow-y-auto">
                                  {getSocialPosts(template.id, pageUrl).linkedin}
                                </pre>
                              </div>
                              
                              {/* Instagram */}
                              <div className="bg-white rounded-lg p-4 border border-blue-200">
                                <div className="flex items-center justify-between mb-2">
                                  <div className="flex items-center gap-2">
                                    <div className="w-8 h-8 bg-gradient-to-br from-blue-500 via-blue-400 to-blue-600 rounded-full flex items-center justify-center">
                                      <Instagram className="h-4 w-4 text-white" />
                                    </div>
                                    <span className="font-semibold text-blue-800">Instagram</span>
                                  </div>
                                  <Button
                                    size="sm"
                                    onClick={() => copyPostToClipboard(getSocialPosts(template.id, pageUrl).instagram, `${template.id}-ig`)}
                                    className={copiedPost === `${template.id}-ig` ? 'bg-blue-600' : 'bg-blue-600'}
                                  >
                                    {copiedPost === `${template.id}-ig` ? '✓ Copied!' : 'Copy Post'}
                                  </Button>
                                </div>
                                <pre className="text-xs text-slate-700 whitespace-pre-wrap bg-slate-50 p-3 rounded-lg max-h-32 overflow-y-auto">
                                  {getSocialPosts(template.id, pageUrl).instagram}
                                </pre>
                              </div>
                            </div>
                            
                            <div className="mt-4 p-3 bg-slate-50 rounded-lg border border-slate-200">
                              <p className="text-xs text-slate-600">
                                <strong>💡 Pro Tip:</strong> Post at different times on each platform. 
                                Best times: Facebook (1-4pm), Instagram (11am-1pm), LinkedIn (7-8am or 5-6pm).
                              </p>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                {/* Business-Specific Templates */}
                <h4 className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-3 flex items-center gap-2">
                  <Target className="h-4 w-4" />
                  Industry-Specific Templates
                  <Badge className="bg-blue-100 text-blue-700 text-xs">Higher Conversions!</Badge>
                </h4>
                <p className="text-sm text-slate-600 mb-4">
                  Use these templates when targeting specific industries - they include tailored messaging and pain points.
                </p>
                <div className="grid gap-4">
                  {[
                    { 
                      id: 'auto-dealership', 
                      name: '🚗 Auto Dealerships', 
                      description: 'Perfect for car dealers - focuses on inventory visibility, local search, and "near me" searches',
                      color: 'from-blue-600 to-slate-700',
                      preview: 'Automotive imagery, showroom vibes',
                      industries: ['Car Dealerships', 'Used Cars', 'Auto Sales']
                    },
                    { 
                      id: 'restaurant', 
                      name: '🍽️ Restaurants & Food Service', 
                      description: 'Highlights "best restaurants near me" searches, reviews, and AI recommendations for dining',
                      color: 'from-slate-600 to-slate-800',
                      preview: 'Warm, appetizing colors',
                      industries: ['Restaurants', 'Cafes', 'Food Trucks', 'Bars']
                    },
                    { 
                      id: 'dental', 
                      name: '🦷 Dental Practices', 
                      description: 'Focuses on patient acquisition, "dentist near me" searches, and trust-building',
                      color: 'from-cyan-500 to-blue-600',
                      preview: 'Clean, clinical, trustworthy',
                      industries: ['Dentists', 'Orthodontists', 'Oral Surgeons']
                    },
                    { 
                      id: 'medical', 
                      name: '⚕️ Medical & Healthcare', 
                      description: 'For doctors, clinics, and healthcare providers - HIPAA-conscious, professional tone',
                      color: 'from-blue-500 to-slate-600',
                      preview: 'Professional medical aesthetic',
                      industries: ['Doctors', 'Clinics', 'Urgent Care', 'Specialists']
                    },
                    { 
                      id: 'home-services', 
                      name: '🏠 Home Services', 
                      description: 'Plumbers, electricians, HVAC - emergency search visibility, local trust signals',
                      color: 'from-slate-500 to-blue-600',
                      preview: 'Bold, trustworthy, urgent',
                      industries: ['Plumbers', 'Electricians', 'HVAC', 'Roofers']
                    },
                    { 
                      id: 'legal', 
                      name: '⚖️ Law Firms & Attorneys', 
                      description: 'Professional, authoritative - focuses on practice area visibility and client acquisition',
                      color: 'from-slate-700 to-slate-900',
                      preview: 'Sophisticated, trustworthy',
                      industries: ['Lawyers', 'Law Firms', 'Attorneys']
                    },
                    { 
                      id: 'real-estate', 
                      name: '🏡 Real Estate', 
                      description: 'For realtors and brokerages - local market visibility, buyer/seller leads',
                      color: 'from-blue-600 to-slate-600',
                      preview: 'Property-focused, aspirational',
                      industries: ['Realtors', 'Brokers', 'Property Managers']
                    },
                    { 
                      id: 'fitness', 
                      name: '💪 Fitness & Gyms', 
                      description: 'Gyms, personal trainers, studios - membership acquisition, local fitness searches',
                      color: 'from-blue-500 to-blue-700',
                      preview: 'Energetic, motivational',
                      industries: ['Gyms', 'Personal Trainers', 'Yoga Studios', 'CrossFit']
                    },
                    { 
                      id: 'salon-spa', 
                      name: '💅 Salons & Spas', 
                      description: 'Beauty services - appointment bookings, "near me" searches, reviews focus',
                      color: 'from-blue-400 to-slate-500',
                      preview: 'Elegant, beauty-focused',
                      industries: ['Hair Salons', 'Spas', 'Nail Salons', 'Barbershops']
                    },
                    { 
                      id: 'pet-services', 
                      name: '🐾 Pet Services', 
                      description: 'Vets, groomers, pet stores - pet parent targeting, local pet searches',
                      color: 'from-slate-400 to-blue-500',
                      preview: 'Friendly, pet-loving vibes',
                      industries: ['Veterinarians', 'Pet Groomers', 'Pet Stores', 'Dog Walkers']
                    },
                    { 
                      id: 'gift-portfolio', 
                      name: '🎓 Gift a Portfolio', 
                      description: 'Graduation gift landing page - prepaid portfolio websites with custom domains from $109',
                      color: 'from-blue-500 to-cyan-600',
                      preview: 'Graduation themed, gift-focused',
                      industries: ['College Grads', 'Medical School', 'Dental School', 'Law School'],
                      directUrl: '/gift-portfolio'
                    },
                    { 
                      id: 'ppc-audit', 
                      name: '📊 Free PPC Audit', 
                      description: 'Lead magnet - free Google Ads health check, captures PPC agency intel',
                      color: 'from-blue-600 to-slate-700',
                      preview: 'Data-driven, savings-focused',
                      industries: ['Google Ads', 'PPC Management', 'SEM', 'Ad Agencies'],
                      directUrl: '/ppc-audit'
                    },
                    { 
                      id: 'social-media', 
                      name: '📱 Social Media Management', 
                      description: 'Hootsuite alternative - TikTok, Facebook, Instagram publishing from $49/mo',
                      color: 'from-blue-500 to-blue-700',
                      preview: 'Modern, multi-platform',
                      industries: ['Social Media', 'Content Marketing', 'Influencers', 'Agencies'],
                      directUrl: '/social-media-pricing'
                    }
                  ].map((template) => {
                    const templateClicks = templateAnalytics?.summary?.template_breakdown?.[template.id] || 0;
                    const pageUrl = template.directUrl 
                      ? `https://upost.com${template.directUrl}?ref=${affiliateCode}`
                      : `https://upost.com/p/${affiliateCode}/${template.id}`;
                    
                    return (
                      <div 
                        key={template.id}
                        className="border-2 border-dashed border-slate-200 rounded-lg p-4 hover:border-blue-400 hover:bg-blue-50/30 transition-all"
                      >
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                          <div className="flex items-start gap-4">
                            <div className={`w-12 h-12 rounded-lg bg-gradient-to-br ${template.color} flex items-center justify-center shrink-0`}>
                              <Building className="h-6 w-6 text-white" />
                            </div>
                            <div>
                              <h3 className="font-semibold text-slate-900">{template.name}</h3>
                              <p className="text-sm text-slate-600">{template.description}</p>
                              <div className="flex flex-wrap gap-1 mt-2">
                                {template.industries.map((ind, idx) => (
                                  <Badge key={idx} variant="outline" className="text-xs border-slate-300">
                                    {ind}
                                  </Badge>
                                ))}
                              </div>
                              {templateClicks > 0 && (
                                <Badge className="mt-2 bg-blue-100 text-blue-700">
                                  {templateClicks} clicks
                                </Badge>
                              )}
                            </div>
                          </div>
                          
                          <div className="flex flex-wrap items-center gap-2 sm:shrink-0">
                            <a 
                              href={pageUrl} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 px-3 py-2 text-sm border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors"
                            >
                              <ExternalLink className="h-4 w-4" />
                              Preview
                            </a>
                            <Button
                              onClick={() => copyLandingPageLink(template.id)}
                              className={copiedLandingPage === template.id ? 'bg-blue-600' : 'bg-blue-600 hover:bg-blue-700'}
                              size="sm"
                            >
                              {copiedLandingPage === template.id ? (
                                <>
                                  <CheckCircle className="h-4 w-4 mr-1" />
                                  Copied!
                                </>
                              ) : (
                                <>
                                  <Copy className="h-4 w-4 mr-1" />
                                  Copy Link
                                </>
                              )}
                            </Button>
                            <Button
                              onClick={() => setShowSocialPosts(showSocialPosts === template.id ? null : template.id)}
                              variant={showSocialPosts === template.id ? "default" : "outline"}
                              size="sm"
                              className={showSocialPosts === template.id ? 'bg-blue-600 hover:bg-blue-700' : 'border-blue-300 text-blue-700 hover:bg-blue-50'}
                            >
                              <MessageSquare className="h-4 w-4 mr-1" />
                              {showSocialPosts === template.id ? 'Hide Posts' : 'Get Posts'}
                            </Button>
                          </div>
                        </div>
                        
                        {/* Social Media Posts Panel - "So simple even a Sloth can do it!" */}
                        {showSocialPosts === template.id && (
                          <div className="mt-4 p-4 bg-gradient-to-br from-blue-50 to-slate-50 rounded-xl border border-blue-200">
                            <div className="flex items-center gap-2 mb-4">
                              <span className="text-2xl">🦥</span>
                              <div>
                                <h4 className="font-bold text-slate-900">Ready-to-Post Content</h4>
                                <p className="text-xs text-slate-600">So simple even a Sloth can do it in his underwear!</p>
                              </div>
                            </div>
                            
                            <div className="grid gap-4">
                              {/* Facebook */}
                              <div className="bg-white rounded-lg p-4 border border-blue-200">
                                <div className="flex items-center justify-between mb-2">
                                  <div className="flex items-center gap-2">
                                    <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center">
                                      <Facebook className="h-4 w-4 text-white" />
                                    </div>
                                    <span className="font-semibold text-blue-800">Facebook</span>
                                  </div>
                                  <Button
                                    size="sm"
                                    onClick={() => copyPostToClipboard(getSocialPosts(template.id, pageUrl).facebook, `${template.id}-fb`)}
                                    className={copiedPost === `${template.id}-fb` ? 'bg-blue-600' : 'bg-blue-600'}
                                  >
                                    {copiedPost === `${template.id}-fb` ? '✓ Copied!' : 'Copy Post'}
                                  </Button>
                                </div>
                                <pre className="text-xs text-slate-700 whitespace-pre-wrap bg-slate-50 p-3 rounded-lg max-h-32 overflow-y-auto">
                                  {getSocialPosts(template.id, pageUrl).facebook}
                                </pre>
                              </div>
                              
                              {/* Instagram */}
                              <div className="bg-white rounded-lg p-4 border border-blue-200">
                                <div className="flex items-center justify-between mb-2">
                                  <div className="flex items-center gap-2">
                                    <div className="w-8 h-8 bg-gradient-to-br from-blue-500 via-blue-400 to-blue-600 rounded-full flex items-center justify-center">
                                      <Instagram className="h-4 w-4 text-white" />
                                    </div>
                                    <span className="font-semibold text-blue-800">Instagram</span>
                                    <Badge className="bg-blue-100 text-blue-700 text-xs">Younger Market</Badge>
                                  </div>
                                  <Button
                                    size="sm"
                                    onClick={() => copyPostToClipboard(getSocialPosts(template.id, pageUrl).instagram, `${template.id}-ig`)}
                                    className={copiedPost === `${template.id}-ig` ? 'bg-blue-600' : 'bg-blue-600'}
                                  >
                                    {copiedPost === `${template.id}-ig` ? '✓ Copied!' : 'Copy Post'}
                                  </Button>
                                </div>
                                <pre className="text-xs text-slate-700 whitespace-pre-wrap bg-slate-50 p-3 rounded-lg max-h-32 overflow-y-auto">
                                  {getSocialPosts(template.id, pageUrl).instagram}
                                </pre>
                              </div>
                              
                              {/* LinkedIn */}
                              <div className="bg-white rounded-lg p-4 border border-blue-300">
                                <div className="flex items-center justify-between mb-2">
                                  <div className="flex items-center gap-2">
                                    <div className="w-8 h-8 bg-blue-700 rounded-full flex items-center justify-center">
                                      <Linkedin className="h-4 w-4 text-white" />
                                    </div>
                                    <span className="font-semibold text-blue-800">LinkedIn</span>
                                    <Badge className="bg-blue-100 text-blue-700 text-xs">Professional</Badge>
                                  </div>
                                  <Button
                                    size="sm"
                                    onClick={() => copyPostToClipboard(getSocialPosts(template.id, pageUrl).linkedin, `${template.id}-li`)}
                                    className={copiedPost === `${template.id}-li` ? 'bg-blue-600' : 'bg-blue-700'}
                                  >
                                    {copiedPost === `${template.id}-li` ? '✓ Copied!' : 'Copy Post'}
                                  </Button>
                                </div>
                                <pre className="text-xs text-slate-700 whitespace-pre-wrap bg-slate-50 p-3 rounded-lg max-h-32 overflow-y-auto">
                                  {getSocialPosts(template.id, pageUrl).linkedin}
                                </pre>
                              </div>
                            </div>
                            
                            <div className="mt-4 p-3 bg-slate-50 rounded-lg border border-slate-200">
                              <p className="text-xs text-slate-600">
                                <strong>💡 Pro Tip:</strong> Post at different times on each platform. 
                                Best times: Facebook (1-4pm), Instagram (11am-1pm), LinkedIn (7-8am or 5-6pm).
                              </p>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>

            {/* Custom Template Generator */}
            <Card className="border-2 border-dashed border-blue-300 bg-gradient-to-br from-blue-50 to-slate-50">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Wand2 className="h-5 w-5 text-blue-600" />
                  Create Custom Template
                  <Badge className="bg-gradient-to-r from-blue-500 to-blue-600 text-white">AI-Powered</Badge>
                </CardTitle>
                <CardDescription>
                  Generate your own unique landing page design with AI
                </CardDescription>
              </CardHeader>
              <CardContent>
                <button
                  onClick={() => setShowTemplateGenerator(!showTemplateGenerator)}
                  className="w-full p-4 border-2 border-dashed border-blue-200 rounded-lg hover:border-blue-400 hover:bg-blue-50 transition-all flex items-center justify-center gap-3 group"
                >
                  {showTemplateGenerator ? (
                    <>
                      <ChevronUp className="h-5 w-5 text-blue-500" />
                      <span className="font-medium text-blue-700">Hide Generator</span>
                    </>
                  ) : (
                    <>
                      <Sparkles className="h-5 w-5 text-blue-500 group-hover:animate-pulse" />
                      <span className="font-medium text-blue-700">Generate New Template</span>
                    </>
                  )}
                </button>

                {showTemplateGenerator && (
                  <div className="mt-6 space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">
                          Template Name *
                        </label>
                        <Input
                          placeholder="e.g., My High-Converting Page"
                          value={customTemplateConfig.name}
                          onChange={(e) => setCustomTemplateConfig({...customTemplateConfig, name: e.target.value})}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">
                          Primary Color
                        </label>
                        <div className="flex gap-2">
                          <Input
                            type="color"
                            value={customTemplateConfig.primaryColor}
                            onChange={(e) => setCustomTemplateConfig({...customTemplateConfig, primaryColor: e.target.value})}
                            className="w-14 h-10 p-1 cursor-pointer"
                          />
                          <Input
                            value={customTemplateConfig.primaryColor}
                            onChange={(e) => setCustomTemplateConfig({...customTemplateConfig, primaryColor: e.target.value})}
                            className="flex-1"
                          />
                        </div>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">
                        Main Headline *
                      </label>
                      <Input
                        placeholder="e.g., Is AI Recommending Your Business?"
                        value={customTemplateConfig.headline}
                        onChange={(e) => setCustomTemplateConfig({...customTemplateConfig, headline: e.target.value})}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">
                        Subheadline
                      </label>
                      <Input
                        placeholder="e.g., 60% of consumers now ask AI for recommendations"
                        value={customTemplateConfig.subheadline}
                        onChange={(e) => setCustomTemplateConfig({...customTemplateConfig, subheadline: e.target.value})}
                      />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">
                          Style
                        </label>
                        <select
                          value={customTemplateConfig.style}
                          onChange={(e) => setCustomTemplateConfig({...customTemplateConfig, style: e.target.value})}
                          className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm"
                        >
                          <option value="modern">Modern</option>
                          <option value="minimal">Minimal</option>
                          <option value="bold">Bold</option>
                          <option value="elegant">Elegant</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">
                          Tone
                        </label>
                        <select
                          value={customTemplateConfig.tone}
                          onChange={(e) => setCustomTemplateConfig({...customTemplateConfig, tone: e.target.value})}
                          className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm"
                        >
                          <option value="professional">Professional</option>
                          <option value="casual">Casual & Friendly</option>
                          <option value="urgent">Urgent/FOMO</option>
                          <option value="friendly">Warm & Inviting</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">
                          CTA Button Text
                        </label>
                        <Input
                          placeholder="Get Started Free"
                          value={customTemplateConfig.ctaText}
                          onChange={(e) => setCustomTemplateConfig({...customTemplateConfig, ctaText: e.target.value})}
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">
                        Key Benefits to Highlight (optional)
                      </label>
                      <Textarea
                        placeholder="Enter benefits to emphasize, one per line..."
                        value={customTemplateConfig.keyBenefits}
                        onChange={(e) => setCustomTemplateConfig({...customTemplateConfig, keyBenefits: e.target.value})}
                        rows={2}
                      />
                    </div>

                    <div className="flex gap-3">
                      <Button
                        onClick={generateCustomTemplate}
                        disabled={generatingTemplate || !customTemplateConfig.name.trim() || !customTemplateConfig.headline.trim()}
                        className="bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700"
                      >
                        {generatingTemplate ? (
                          <Loader2 className="h-4 w-4 animate-spin mr-2" />
                        ) : (
                          <Wand2 className="h-4 w-4 mr-2" />
                        )}
                        Generate Template
                      </Button>
                    </div>

                    {/* Generated Template Preview */}
                    {generatedTemplatePreview && (
                      <div className="mt-6 border-2 border-blue-300 rounded-lg bg-white overflow-hidden">
                        <div className="bg-blue-50 border-b border-blue-200 px-4 py-3 flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <CheckCircle className="h-5 w-5 text-blue-600" />
                            <span className="font-medium text-blue-800">Template Generated!</span>
                          </div>
                          <Badge className="bg-blue-100 text-blue-700">Preview</Badge>
                        </div>
                        
                        <div className="p-4">
                          {/* Mini Preview */}
                          <div 
                            className="rounded-lg p-6 mb-4 text-white text-center"
                            style={{ 
                              background: `linear-gradient(135deg, ${generatedTemplatePreview.style?.gradientFrom || customTemplateConfig.primaryColor}, ${generatedTemplatePreview.style?.gradientTo || '#4f46e5'})` 
                            }}
                          >
                            <h3 className="text-xl font-bold mb-2">{generatedTemplatePreview.hero?.headline}</h3>
                            <p className="text-sm opacity-90 mb-4">{generatedTemplatePreview.hero?.subheadline}</p>
                            <div className="inline-block bg-white text-slate-900 px-4 py-2 rounded-lg font-medium text-sm">
                              {generatedTemplatePreview.hero?.ctaText || customTemplateConfig.ctaText}
                            </div>
                          </div>

                          {/* Stats Preview */}
                          {generatedTemplatePreview.stats && (
                            <div className="grid grid-cols-3 gap-2 mb-4">
                              {generatedTemplatePreview.stats.map((stat, idx) => (
                                <div key={idx} className="text-center p-2 bg-slate-50 rounded">
                                  <div className="font-bold text-lg" style={{ color: customTemplateConfig.primaryColor }}>{stat.value}</div>
                                  <div className="text-xs text-slate-600">{stat.label}</div>
                                </div>
                              ))}
                            </div>
                          )}

                          {/* Benefits Preview */}
                          {generatedTemplatePreview.benefits && (
                            <div className="space-y-2 mb-4">
                              {generatedTemplatePreview.benefits.slice(0, 3).map((benefit, idx) => (
                                <div key={idx} className="flex items-start gap-2 text-sm">
                                  <CheckCircle className="h-4 w-4 text-blue-500 mt-0.5 shrink-0" />
                                  <span><strong>{benefit.title}:</strong> {benefit.description}</span>
                                </div>
                              ))}
                            </div>
                          )}

                          <div className="flex gap-3">
                            <Button
                              onClick={saveCustomTemplate}
                              disabled={savingTemplate}
                              className="bg-blue-600 hover:bg-blue-700"
                            >
                              {savingTemplate ? (
                                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                              ) : (
                                <Save className="h-4 w-4 mr-2" />
                              )}
                              Save & Publish
                            </Button>
                            <Button
                              variant="outline"
                              onClick={() => setGeneratedTemplatePreview(null)}
                            >
                              Regenerate
                            </Button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Saved Custom Templates */}
                {customTemplates.length > 0 && (
                  <div className="mt-6 pt-6 border-t border-blue-200">
                    <h4 className="font-medium text-slate-900 mb-3">Your Custom Templates</h4>
                    <div className="space-y-3">
                      {customTemplates.map((template) => (
                        <div 
                          key={template.id}
                          className="border rounded-lg p-4 bg-white flex items-center justify-between"
                        >
                          <div className="flex items-center gap-3">
                            <div 
                              className="w-10 h-10 rounded-lg"
                              style={{ 
                                background: `linear-gradient(135deg, ${template.style?.gradientFrom || template.config?.primaryColor || '#7c3aed'}, ${template.style?.gradientTo || '#4f46e5'})` 
                              }}
                            />
                            <div>
                              <p className="font-medium text-slate-900">{template.name}</p>
                              <p className="text-xs text-slate-500">{template.clicks || 0} clicks</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <a
                              href={`https://upost.com/p/${affiliateCode}/custom-${template.id}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="p-2 hover:bg-slate-100 rounded"
                            >
                              <Eye className="h-4 w-4 text-slate-600" />
                            </a>
                            <button
                              onClick={() => {
                                navigator.clipboard.writeText(`https://upost.com/p/${affiliateCode}/custom-${template.id}`);
                              }}
                              className="p-2 hover:bg-slate-100 rounded"
                            >
                              <Copy className="h-4 w-4 text-slate-600" />
                            </button>
                            <button
                              onClick={() => deleteCustomTemplate(template.id)}
                              className="p-2 hover:bg-red-100 rounded"
                            >
                              <Trash2 className="h-4 w-4 text-red-500" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* My Created Landing Pages */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <FileText className="h-5 w-5 text-blue-600" />
                      My Landing Pages
                    </CardTitle>
                    <CardDescription>
                      Manage landing pages you've created for your specific industry
                    </CardDescription>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={fetchMyLandingPages}
                    disabled={landingPagesLoading}
                  >
                    <RefreshCw className={`h-4 w-4 mr-1 ${landingPagesLoading ? 'animate-spin' : ''}`} />
                    Refresh
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {landingPagesLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
                    <span className="ml-2 text-slate-500">Loading pages...</span>
                  </div>
                ) : myLandingPages.length === 0 ? (
                  <div className="text-center py-8 bg-slate-50 rounded-lg border-2 border-dashed border-slate-200">
                    <Globe className="h-12 w-12 mx-auto text-slate-300 mb-3" />
                    <h3 className="text-lg font-medium text-slate-700 mb-1">No Custom Landing Pages Yet</h3>
                    <p className="text-sm text-slate-500 mb-4">
                      Create a custom landing page from the templates above to get started
                    </p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {myLandingPages.map((page) => {
                      const statusColors = {
                        draft: 'bg-slate-100 text-slate-700',
                        pending: 'bg-slate-100 text-slate-700',
                        approved: 'bg-blue-100 text-blue-700',
                        rejected: 'bg-red-100 text-red-700'
                      };
                      const statusIcons = {
                        draft: <FileText className="h-4 w-4" />,
                        pending: <Clock className="h-4 w-4" />,
                        approved: <CheckCircle className="h-4 w-4" />,
                        rejected: <AlertCircle className="h-4 w-4" />
                      };
                      
                      return (
                        <div
                          key={page.page_id}
                          className="border rounded-lg p-4 hover:border-blue-300 transition-colors"
                        >
                          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                            <div className="flex items-start gap-3">
                              <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center shrink-0">
                                <Globe className="h-6 w-6 text-white" />
                              </div>
                              <div>
                                <h3 className="font-semibold text-slate-900 capitalize">
                                  {page.template_id?.replace(/-/g, ' ') || 'Custom Page'}
                                </h3>
                                <p className="text-xs text-slate-500 mb-2">
                                  Created: {new Date(page.created_at).toLocaleDateString()}
                                </p>
                                <Badge className={`${statusColors[page.status]} flex items-center gap-1 w-fit`}>
                                  {statusIcons[page.status]}
                                  {page.status.charAt(0).toUpperCase() + page.status.slice(1)}
                                </Badge>
                              </div>
                            </div>
                            
                            <div className="flex items-center gap-2">
                              {/* Preview button */}
                              <a
                                href={`${API_URL}/api/affiliate/landing-page/preview/${page.page_id}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1 px-3 py-2 text-sm border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors"
                              >
                                <Eye className="h-4 w-4" />
                                Preview
                              </a>
                              
                              {/* Choose OG Image button */}
                              <Button
                                variant="outline"
                                onClick={() => openOgSelector(page)}
                                className="text-blue-600 border-blue-200 hover:bg-blue-50"
                              >
                                <ImageIcon className="h-4 w-4 mr-1" />
                                {page.og_style_id ? 'Change' : 'Choose'} OG Image
                              </Button>
                              
                              {/* Submit for approval (only for drafts) */}
                              {page.status === 'draft' && (
                                <Button
                                  onClick={() => submitPageForApproval(page.page_id)}
                                  disabled={submittingPage === page.page_id}
                                  className="bg-blue-600 hover:bg-blue-700"
                                >
                                  {submittingPage === page.page_id ? (
                                    <Loader2 className="h-4 w-4 animate-spin mr-1" />
                                  ) : (
                                    <Share2 className="h-4 w-4 mr-1" />
                                  )}
                                  Submit for Approval
                                </Button>
                              )}
                              
                              {/* Copy live URL (only for approved) */}
                              {page.status === 'approved' && page.live_url && (
                                <Button
                                  onClick={() => {
                                    navigator.clipboard.writeText(page.live_url);
                                    alert('Live URL copied!');
                                  }}
                                  className="bg-blue-600 hover:bg-blue-700"
                                >
                                  <Copy className="h-4 w-4 mr-1" />
                                  Copy Live URL
                                </Button>
                              )}
                              
                              {/* Pending status message */}
                              {page.status === 'pending' && (
                                <span className="text-sm text-slate-600 bg-slate-50 px-3 py-2 rounded-lg">
                                  Awaiting admin review
                                </span>
                              )}
                              
                              {/* Rejection message */}
                              {page.status === 'rejected' && page.rejection_reason && (
                                <span className="text-sm text-red-700 bg-red-50 px-3 py-2 rounded-lg max-w-xs truncate">
                                  {page.rejection_reason}
                                </span>
                              )}
                            </div>
                          </div>
                          
                          {/* OG Image Selector Modal */}
                          {showOgSelector === page.page_id && (
                            <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                              <div className="flex items-center justify-between mb-4">
                                <div>
                                  <h4 className="font-semibold text-slate-900 flex items-center gap-2">
                                    <ImageIcon className="h-5 w-5 text-blue-600" />
                                    Choose Your Social Media Image
                                  </h4>
                                  <p className="text-sm text-slate-600">
                                    This image appears when your page is shared on LinkedIn, Facebook, or Twitter
                                  </p>
                                </div>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => setShowOgSelector(null)}
                                >
                                  ✕
                                </Button>
                              </div>
                              
                              {ogOptions[page.template_id] ? (
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                  {ogOptions[page.template_id].map((style) => (
                                    <div
                                      key={style.style_id}
                                      onClick={() => setSelectedOgStyle(style.style_id)}
                                      className={`cursor-pointer border-2 rounded-lg p-3 transition-all ${
                                        selectedOgStyle === style.style_id 
                                          ? 'border-blue-500 bg-blue-100' 
                                          : 'border-slate-200 hover:border-blue-300 bg-white'
                                      }`}
                                    >
                                      {/* Preview thumbnail */}
                                      <div className="aspect-video bg-gradient-to-br from-slate-800 to-slate-900 rounded-lg mb-3 overflow-hidden relative">
                                        <img
                                          src={`${API_URL}/api/og/industry/${page.template_id}/${style.style_id}?affiliate_code=${affiliateCode}&affiliate_name=${encodeURIComponent(localStorage.getItem('affiliate_name') || 'Partner')}`}
                                          alt={style.name}
                                          className="w-full h-full object-cover"
                                          loading="lazy"
                                          onError={(e) => {
                                            e.target.style.display = 'none';
                                            e.target.parentElement.innerHTML = `<div class="flex items-center justify-center h-full text-white text-xs">${style.name}</div>`;
                                          }}
                                        />
                                        {selectedOgStyle === style.style_id && (
                                          <div className="absolute top-2 right-2 bg-blue-600 text-white rounded-full p-1">
                                            <CheckCircle className="h-4 w-4" />
                                          </div>
                                        )}
                                      </div>
                                      <h5 className="font-medium text-slate-900">{style.name}</h5>
                                      <p className="text-xs text-slate-500">{style.description}</p>
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <div className="flex items-center justify-center py-8">
                                  <Loader2 className="h-6 w-6 animate-spin text-blue-500" />
                                  <span className="ml-2 text-slate-500">Loading image options...</span>
                                </div>
                              )}
                              
                              <div className="mt-4 flex justify-end gap-2">
                                <Button
                                  variant="outline"
                                  onClick={() => setShowOgSelector(null)}
                                >
                                  Cancel
                                </Button>
                                <Button
                                  onClick={() => saveOgStyle(page.page_id, selectedOgStyle, page.template_id)}
                                  disabled={!selectedOgStyle || savingOgStyle}
                                  className="bg-blue-600 hover:bg-blue-700"
                                >
                                  {savingOgStyle ? (
                                    <Loader2 className="h-4 w-4 animate-spin mr-1" />
                                  ) : (
                                    <CheckCircle className="h-4 w-4 mr-1" />
                                  )}
                                  Save Selection
                                </Button>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Tips for Using Landing Pages */}
            <Card className="border-blue-200 bg-blue-50">
              <CardHeader>
                <CardTitle className="text-blue-800">Tips for Maximum Conversions</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 text-sm text-blue-900">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-blue-600 mt-0.5 shrink-0" />
                    <span><strong>Test different templates</strong> — share each one and see which gets more clicks</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-blue-600 mt-0.5 shrink-0" />
                    <span><strong>Match template to audience</strong> — "Urgency" works for deal-seekers, "Case Study" for skeptics</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-blue-600 mt-0.5 shrink-0" />
                    <span><strong>Share on social media</strong> — these pages have beautiful OG images for previews</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-blue-600 mt-0.5 shrink-0" />
                    <span><strong>Use in email signatures</strong> — subtle but effective way to get consistent traffic</span>
                  </li>
                </ul>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Refer Partners Tab - THE KEY FEATURE */}
          <TabsContent value="refer-partners" className="space-y-6">
            {/* Explanation Banner */}
            <Card className="bg-gradient-to-r from-blue-600 via-blue-700 to-cyan-600 text-white">
              <CardContent className="pt-6 pb-6">
                <div className="text-center max-w-2xl mx-auto">
                  <UserPlus className="h-12 w-12 mx-auto mb-4 opacity-90" />
                  <h2 className="text-2xl font-bold mb-2">Build Your Team, Earn More</h2>
                  <p className="text-blue-100 mb-4">
                    Recruit other affiliates and earn <span className="text-white font-bold">10% of their commissions</span> forever!
                    When they make money, you make money too.
                  </p>
                  <div className="bg-white/20 rounded-lg p-4 inline-block">
                    <p className="text-sm text-blue-100">Example: Your recruited partner earns $100 → You get <span className="text-white font-bold">$10 bonus</span></p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Partner Referral Link */}
            <Card className="border-2 border-blue-200">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-blue-700">
                  <Gift className="h-5 w-5" />
                  Your Partner Recruitment Link
                </CardTitle>
                <CardDescription>
                  Share this link with people who want to become affiliates. You'll earn 10% of ALL their future commissions!
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex gap-2 mb-4">
                  <Input 
                    value={stats.partner_referral_link} 
                    readOnly 
                    className="font-mono text-sm bg-blue-50 border-blue-200"
                  />
                  <Button 
                    onClick={() => copyToClipboard(stats.partner_referral_link, 'partner')} 
                    variant={copiedPartner ? "default" : "outline"}
                    className={copiedPartner ? "bg-blue-600" : "border-blue-300 text-blue-700 hover:bg-blue-50"}
                  >
                    {copiedPartner ? <CheckCircle className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                  </Button>
                </div>
                <div className="bg-blue-50 rounded-lg p-4">
                  <p className="text-sm text-blue-800">
                    <strong>How it works:</strong> When someone applies using your link and gets approved, 
                    they become part of your team. Every time they earn a commission, you automatically get 10% as a bonus.
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Team Stats */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Card>
                <CardContent className="pt-6 text-center">
                  <UserPlus className="h-10 w-10 mx-auto mb-2 text-blue-600" />
                  <p className="text-3xl font-bold text-slate-900">{teamMembers.length}</p>
                  <p className="text-sm text-slate-500">Partners Recruited</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6 text-center">
                  <DollarSign className="h-10 w-10 mx-auto mb-2 text-blue-600" />
                  <p className="text-3xl font-bold text-blue-600">${teamEarnings.toFixed(2)}</p>
                  <p className="text-sm text-slate-500">Team Bonus Earned</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6 text-center">
                  <TrendingUp className="h-10 w-10 mx-auto mb-2 text-blue-600" />
                  <p className="text-3xl font-bold text-slate-900">10%</p>
                  <p className="text-sm text-slate-500">Your Bonus Rate</p>
                </CardContent>
              </Card>
            </div>

            {/* Team Members List */}
            <Card>
              <CardHeader>
                <CardTitle>Your Team Members</CardTitle>
                <CardDescription>Affiliates you've recruited who are earning commissions for you</CardDescription>
              </CardHeader>
              <CardContent>
                {teamMembers.length > 0 ? (
                  <div className="space-y-3">
                    {teamMembers.map((member, idx) => (
                      <div key={idx} className="flex items-center justify-between p-4 bg-blue-50 rounded-lg border border-blue-100">
                        <div className="flex items-center">
                          <div className="w-10 h-10 bg-blue-200 rounded-full flex items-center justify-center mr-3">
                            <Users className="h-5 w-5 text-blue-700" />
                          </div>
                          <div>
                            <p className="font-medium text-slate-900">{member.name}</p>
                            <p className="text-xs text-slate-500">
                              Joined {new Date(member.created_at).toLocaleDateString()}
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-sm text-slate-500">Their earnings</p>
                          <p className="font-semibold text-slate-900">${(member.total_earnings || 0).toFixed(2)}</p>
                          <p className="text-xs text-blue-600">Your bonus: ${((member.total_earnings || 0) * 0.1).toFixed(2)}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <UserPlus className="h-16 w-16 text-blue-200 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-slate-900 mb-2">No team members yet</h3>
                    <p className="text-slate-500 mb-4 max-w-md mx-auto">
                      Share your partner recruitment link with friends, colleagues, or your audience to start building your team!
                    </p>
                    <Button 
                      onClick={() => copyToClipboard(stats.partner_referral_link, 'partner')}
                      className="bg-blue-600 hover:bg-blue-700"
                    >
                      <Copy className="h-4 w-4 mr-2" />
                      Copy Recruitment Link
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Source Analytics - Which platforms are driving signups */}
            {teamMembers.length > 0 && (
              <Card className="border border-blue-200">
                <CardHeader className="bg-gradient-to-r from-blue-50 to-slate-50">
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="flex items-center gap-2 text-slate-800">
                        <BarChart3 className="h-5 w-5 text-blue-600" />
                        Signup Source Analytics
                      </CardTitle>
                      <CardDescription className="text-slate-600">
                        See which platforms are driving the most sub-affiliate signups
                      </CardDescription>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={fetchSourceAnalytics}
                      disabled={sourceAnalyticsLoading}
                      className="border-blue-300 text-blue-700 hover:bg-blue-50"
                    >
                      {sourceAnalyticsLoading ? (
                        <RefreshCw className="h-4 w-4 animate-spin mr-1" />
                      ) : (
                        <RefreshCw className="h-4 w-4 mr-1" />
                      )}
                      Refresh
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="pt-4">
                  {sourceAnalyticsLoading ? (
                    <div className="flex items-center justify-center py-8">
                      <RefreshCw className="h-6 w-6 animate-spin text-blue-600" />
                      <span className="ml-2 text-slate-600">Loading analytics...</span>
                    </div>
                  ) : !sourceAnalytics ? (
                    <div className="text-center py-6 text-slate-500">
                      Click Refresh to see which platforms are driving your signups
                    </div>
                  ) : sourceAnalytics.source_analytics?.length === 0 ? (
                    <div className="text-center py-6 text-slate-500">
                      No source data available yet
                    </div>
                  ) : (
                    <div>
                      {sourceAnalytics.best_performing_source && (
                        <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                          <p className="text-sm text-blue-700">
                            🏆 <strong>Best Performing Source:</strong>{' '}
                            <span className="capitalize">{sourceAnalytics.best_performing_source}</span>
                          </p>
                        </div>
                      )}
                      <div className="space-y-2">
                        {sourceAnalytics.source_analytics.map((src, idx) => (
                          <div key={idx} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center text-sm">
                                {src.source === 'linkedin' && '💼'}
                                {src.source === 'facebook' && '📘'}
                                {src.source === 'instagram' && '📷'}
                                {src.source === 'twitter' && '🐦'}
                                {src.source === 'email' && '📧'}
                                {src.source === 'direct' && '🌐'}
                                {!['linkedin', 'facebook', 'instagram', 'twitter', 'email', 'direct'].includes(src.source) && '❓'}
                              </div>
                              <span className="font-medium capitalize">{src.source}</span>
                            </div>
                            <div className="text-right">
                              <p className="font-semibold">{src.signups} signups</p>
                              <p className="text-xs text-blue-600">${src.earnings_generated} earnings</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* 🎯 Recruitment Landing Pages */}
            <Card className="border-2 border-blue-200">
              <CardHeader className="bg-gradient-to-r from-blue-50 to-slate-50 border-b border-blue-100">
                <CardTitle className="flex items-center gap-2 text-blue-900">
                  <Globe className="h-5 w-5 text-blue-600" />
                  Your Recruitment Landing Pages
                </CardTitle>
                <CardDescription className="text-slate-600">
                  5 professional templates to attract new affiliates. Preview, copy link, and get ready-to-post content!
                </CardDescription>
              </CardHeader>
              <CardContent className="pt-6">
                <div className="grid gap-4">
                  {[
                    { 
                      id: 'default', 
                      name: 'Professional', 
                      desc: 'Clean, corporate look. Great for LinkedIn.', 
                      emoji: '💼',
                      posts: {
                        linkedin: `🚀 Opportunity Alert: Partner with AI-Powered Marketing

I recently became an affiliate for UPost and wanted to share this opportunity.

They help local businesses get recommended by AI assistants like ChatGPT and Google AI.

Partner Benefits:
✅ 20% commission on first sales (up to $1,600)
✅ $30-75/month recurring per referral
✅ 10% bonus on teammates' earnings
✅ Professional marketing materials provided
✅ Hot market - 60% of consumers now use AI for recommendations

Perfect for consultants, marketers, or anyone with business connections.

Learn more: LINK

#AffiliateMarketing #AIMarketing #BusinessOpportunity`,
                        facebook: `💼 New Partnership Opportunity!

I've started working with UPost as an affiliate partner and the earning potential is incredible:

💰 Up to $1,600 per referral
💰 $30-75 monthly recurring
💰 10% bonus when you recruit others

They help businesses get found when people ask AI for recommendations. It's the future of local marketing!

Anyone interested in learning more? Comment below! 👇

LINK`,
                        instagram: `💼 NEW SIDE INCOME 💼

Just became a partner with @webtechservices!

What they do: Help businesses show up in ChatGPT & AI recommendations

What I get:
✅ Up to $1,600/referral
✅ $30-75 monthly recurring forever
✅ 10% team bonuses

DM me "INFO" to learn more!

#sidehustle #affiliatemarketing #passiveincome #workfromhome #entrepreneur`
                      }
                    },
                    { 
                      id: 'money-maker', 
                      name: 'Money Maker', 
                      desc: 'Focus on earnings. Perfect for income-seekers.', 
                      emoji: '💰',
                      posts: {
                        linkedin: `💰 Here's How I'm Building Another Income Stream in 2026

Found an affiliate program that actually pays well:

• $1,600 max per referral (20% commission)
• $30-75 monthly recurring (passive income!)
• 10% of everything my recruits earn

The product? AI visibility for local businesses. 

60% of consumers now use ChatGPT for recommendations. Businesses NEED this.

If you're looking to diversify income, check it out: LINK

#PassiveIncome #SideHustle #WealthBuilding`,
                        facebook: `🤑 Want to know how I'm making extra money in 2026?

I found an affiliate program with INSANE commissions:

💵 Up to $1,600 per sale
💵 $30-75 monthly recurring FOREVER
💵 10% of your team's earnings

The best part? The product sells itself - helping businesses get recommended by ChatGPT and AI.

Who wants the link? Drop a 💰 below!

LINK`,
                        instagram: `💰 SIDE HUSTLE ALERT 💰

Making extra $$ with this affiliate program:

Per sale: Up to $1,600 💵
Recurring: $30-75/month 📈
Team bonus: 10% 🤝

Helping businesses get found by AI.

Link in bio or DM "MONEY" 💰

#makemoney #sidehustle #affiliateprogram #passiveincome #2026goals`
                      }
                    },
                    { 
                      id: 'side-hustle', 
                      name: 'Side Hustle', 
                      desc: 'Approachable style. Great for Facebook/Instagram.', 
                      emoji: '📱',
                      posts: {
                        linkedin: `📱 The Easiest Side Hustle I've Found in 2026

No inventory. No customer service. No selling.

Just share a link and earn:
✅ 20% on first sales
✅ $30-75 monthly recurring 
✅ 10% team bonus

It's for AI marketing - helping local businesses get recommended by ChatGPT.

Super relevant, easy to explain, and people actually want it.

Check it out: LINK

#SideHustle #ExtraIncome #FlexibleWork`,
                        facebook: `🦥 Looking for the EASIEST side hustle ever?

I found one that's literally "share a link, get paid":

💸 Up to $1,600 per referral
💸 $30-75 monthly recurring (forever!)
💸 10% when you recruit others
💸 All materials provided

It's AI marketing for local businesses. Super hot right now!

Comment "INFO" and I'll send you the link! 👇

LINK

#SideHustle #WorkFromHome #EasyMoney`,
                        instagram: `🦥 EASIEST 👏 SIDE 👏 HUSTLE 👏 EVER 👏

Share link → Get paid. That's it.

💰 $1,600 max per sale
📈 $30-75 monthly recurring forever
🤝 10% team bonuses

Helping businesses get found by AI.

DM "HUSTLE" for the link! 🔗

#sidehustle #easymoney #workfromhome #passiveincome #affiliatemarketing`
                      }
                    },
                    { 
                      id: 'influencer', 
                      name: 'Influencer', 
                      desc: 'For content creators. Premium feel.', 
                      emoji: '🎯',
                      posts: {
                        linkedin: `🎯 Content Creators: Here's a Brand Partnership Worth Exploring

UPost has an affiliate program perfect for creators:

What you promote: AI visibility for businesses
What you earn: 
• 20% commission (up to $1,600/sale)
• $30-75 monthly recurring
• 10% team bonus

Why it works: Your audience likely knows business owners who need this. AI search is the future.

Professional materials provided. Authentic promotion, not salesy.

Details: LINK

#ContentCreator #BrandPartnership #Influencer`,
                        facebook: `🎯 Fellow content creators!

Found a partnership opportunity that actually fits our audience:

AI marketing for businesses - helping them get recommended by ChatGPT.

Commission structure:
✨ Up to $1,600 per referral
✨ $30-75 monthly passive income
✨ 10% when your followers join too

They provide all the content - you just share authentically.

DM me if you want details!

LINK`,
                        instagram: `🎯 CREATORS: New partnership alert!

What: AI marketing for businesses
Why: Your followers KNOW business owners

Earnings:
💎 $1,600/referral
💎 $30-75 monthly recurring
💎 10% team bonus

They give you ALL the content.

DM "PARTNER" for details! ✨

#contentcreator #influencer #brandpartnership #sidehustle #creatorseconomy`
                      }
                    },
                    { 
                      id: 'business', 
                      name: 'Business Pro', 
                      desc: 'For agencies & consultants. B2B focused.', 
                      emoji: '🏢',
                      posts: {
                        linkedin: `🏢 Agency Owners & Consultants: Add a Revenue Stream Without Extra Work

I partnered with UPost to offer AI visibility services to my clients.

Here's the model:
• Refer clients who need AI marketing
• Earn 20% commission (up to $1,600)
• $30-75 monthly per referral
• They handle ALL fulfillment

Your clients get a service they need. You get paid. No extra work.

60% of consumers now use AI for business recommendations. This is table stakes.

Learn more: LINK

#AgencyLife #Consulting #RevenueStreams #B2B`,
                        facebook: `🏢 Business consultants & agency owners!

Want to add revenue WITHOUT adding work?

Partner program opportunity:
💼 Refer clients for AI marketing services
💼 Earn 20% (up to $1,600/client)
💼 $30-75 monthly recurring forever
💼 They do ALL the work

Your clients need this - 60% of consumers use AI for recommendations now.

Interested? Comment "AGENCY" 👇

LINK`,
                        instagram: `🏢 AGENCIES & CONSULTANTS 🏢

Add revenue without adding work:

➡️ Refer clients for AI marketing
➡️ Earn up to $1,600/client  
➡️ $30-75 monthly recurring forever
➡️ They handle fulfillment

Your clients need AI visibility.
You get paid.
Win-win.

DM "AGENCY" for details!

#agencylife #consultant #b2b #businessowner #passiveincome`
                      }
                    }
                  ].map((tmpl) => {
                    const recruitUrl = `https://upost.com/r/${affiliateCode}${tmpl.id !== 'default' ? '/' + tmpl.id : ''}`;
                    const postsWithLinks = {
                      linkedin: tmpl.posts.linkedin.replace('LINK', recruitUrl),
                      facebook: tmpl.posts.facebook.replace('LINK', recruitUrl),
                      instagram: tmpl.posts.instagram.replace('LINK', recruitUrl)
                    };
                    
                    return (
                    <div key={tmpl.id} className="border-2 border-blue-100 rounded-xl bg-slate-50 overflow-hidden">
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between p-4 gap-3">
                        <div className="flex items-center gap-3">
                          <span className="text-2xl">{tmpl.emoji}</span>
                          <div>
                            <p className="font-semibold text-slate-900">{tmpl.name}</p>
                            <p className="text-sm text-slate-600">{tmpl.desc}</p>
                          </div>
                        </div>
                        <div className="flex gap-2 shrink-0">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => window.open(`/r/${affiliateCode}${tmpl.id !== 'default' ? '/' + tmpl.id : ''}`, '_blank')}
                            className="border-blue-300 text-blue-700"
                          >
                            <Eye className="h-4 w-4 mr-1" />
                            Preview
                          </Button>
                          <Button
                            size="sm"
                            onClick={() => {
                              navigator.clipboard.writeText(recruitUrl);
                              setCopiedLandingPage(`recruit-${tmpl.id}`);
                              setTimeout(() => setCopiedLandingPage(null), 2000);
                            }}
                            className={copiedLandingPage === `recruit-${tmpl.id}` ? 'bg-blue-600' : 'bg-blue-600'}
                          >
                            {copiedLandingPage === `recruit-${tmpl.id}` ? <><CheckCircle2 className="h-4 w-4 mr-1" /> Copied!</> : <><Copy className="h-4 w-4 mr-1" /> Copy Link</>}
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setShowSocialPosts(showSocialPosts === `recruit-${tmpl.id}` ? null : `recruit-${tmpl.id}`)}
                            className={showSocialPosts === `recruit-${tmpl.id}` ? 'bg-blue-600 text-white' : 'border-blue-300 text-blue-700'}
                          >
                            {showSocialPosts === `recruit-${tmpl.id}` ? <><Share2 className="h-4 w-4 mr-1" /> Hide Posts</> : <><Share2 className="h-4 w-4 mr-1" /> Get Posts</>}
                          </Button>
                        </div>
                      </div>
                      
                      {/* Social Posts Panel */}
                      {showSocialPosts === `recruit-${tmpl.id}` && (
                        <div className="border-t border-blue-200 bg-gradient-to-r from-blue-50 to-slate-50 p-4">
                          <h4 className="font-semibold text-blue-800 mb-3 flex items-center gap-2">
                            <Share2 className="h-4 w-4" />
                            Ready-to-Post Content for {tmpl.name} Template
                          </h4>
                          <div className="grid gap-3">
                            {/* LinkedIn */}
                            <div className="bg-white rounded-lg border border-blue-200 overflow-hidden">
                              <div className="bg-blue-700 text-white px-3 py-2 flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                  <Linkedin className="h-4 w-4" />
                                  <span className="font-medium text-sm">LinkedIn</span>
                                </div>
                                <Button
                                  size="sm"
                                  onClick={() => copyPostToClipboard(postsWithLinks.linkedin, `${tmpl.id}-recruit-li`)}
                                  className={copiedPost === `${tmpl.id}-recruit-li` ? 'bg-blue-600 h-7' : 'bg-blue-800 h-7'}
                                >
                                  {copiedPost === `${tmpl.id}-recruit-li` ? '✓ Copied!' : 'Copy'}
                                </Button>
                              </div>
                              <pre className="text-xs text-slate-700 whitespace-pre-wrap p-3 max-h-28 overflow-y-auto">
                                {postsWithLinks.linkedin}
                              </pre>
                            </div>
                            
                            {/* Facebook */}
                            <div className="bg-white rounded-lg border border-blue-300 overflow-hidden">
                              <div className="bg-blue-600 text-white px-3 py-2 flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                  <Facebook className="h-4 w-4" />
                                  <span className="font-medium text-sm">Facebook</span>
                                </div>
                                <Button
                                  size="sm"
                                  onClick={() => copyPostToClipboard(postsWithLinks.facebook, `${tmpl.id}-recruit-fb`)}
                                  className={copiedPost === `${tmpl.id}-recruit-fb` ? 'bg-blue-600 h-7' : 'bg-blue-700 h-7'}
                                >
                                  {copiedPost === `${tmpl.id}-recruit-fb` ? '✓ Copied!' : 'Copy'}
                                </Button>
                              </div>
                              <pre className="text-xs text-slate-700 whitespace-pre-wrap p-3 max-h-28 overflow-y-auto">
                                {postsWithLinks.facebook}
                              </pre>
                            </div>
                            
                            {/* Instagram */}
                            <div className="bg-white rounded-lg border border-blue-200 overflow-hidden">
                              <div className="bg-gradient-to-r from-blue-500 via-blue-600 to-blue-700 text-white px-3 py-2 flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                  <Instagram className="h-4 w-4" />
                                  <span className="font-medium text-sm">Instagram</span>
                                </div>
                                <Button
                                  size="sm"
                                  onClick={() => copyPostToClipboard(postsWithLinks.instagram, `${tmpl.id}-recruit-ig`)}
                                  className={copiedPost === `${tmpl.id}-recruit-ig` ? 'bg-blue-600 h-7' : 'bg-blue-600 h-7'}
                                >
                                  {copiedPost === `${tmpl.id}-recruit-ig` ? '✓ Copied!' : 'Copy'}
                                </Button>
                              </div>
                              <pre className="text-xs text-slate-700 whitespace-pre-wrap p-3 max-h-28 overflow-y-auto">
                                {postsWithLinks.instagram}
                              </pre>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )})}
                </div>
              </CardContent>
            </Card>

            {/* 🚀 Ready-to-Use Recruitment Content */}
            <Card className="border-2 border-blue-200">
              <CardHeader className="bg-gradient-to-r from-blue-50 to-slate-50 border-b border-blue-100">
                <CardTitle className="flex items-center gap-2 text-slate-800">
                  <Sparkles className="h-5 w-5 text-blue-600" />
                  🦥 Ready-to-Post Recruitment Content
                </CardTitle>
                <CardDescription className="text-slate-600">
                  Copy and paste these posts to recruit affiliates. Each links to the best template for that platform!
                </CardDescription>
              </CardHeader>
              <CardContent className="pt-6 space-y-4">
                {/* LinkedIn Post */}
                <div className="border border-blue-200 rounded-lg overflow-hidden">
                  <div className="bg-blue-700 text-white px-4 py-2 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Linkedin className="h-4 w-4" />
                      <span className="font-medium">LinkedIn</span>
                      <Badge className="bg-blue-800 text-xs">→ Professional Template</Badge>
                    </div>
                    <Button 
                      size="sm"
                      onClick={() => {
                        const recruitUrl = `https://upost.com/r/${affiliateCode}`;
                        const post = `🚀 Side Hustle Opportunity: Get Paid to Share AI Tech

I've been exploring affiliate opportunities and found something interesting:

UPost is looking for partners to spread the word about AI visibility for local businesses.

What caught my attention:
✅ 20% commission on first sale (up to $1,600 per referral)
✅ $30-75 monthly recurring on subscriptions
✅ Ready-made marketing materials provided
✅ No technical skills required
✅ Work from anywhere, anytime

Perfect for:
• Marketing professionals looking for side income
• Business consultants with SMB clients
• Anyone with a network of business owners

It's a growing market - 60% of consumers now use AI for business recommendations.

Apply here: ${recruitUrl}

#SideHustle #AffiliateMarketing #PassiveIncome`;
                        navigator.clipboard.writeText(post);
                        setCopiedLandingPage('linkedin-recruit');
                        setTimeout(() => setCopiedLandingPage(null), 2000);
                      }}
                      className={copiedLandingPage === 'linkedin-recruit' ? 'bg-blue-600' : 'bg-blue-600'}
                    >
                      {copiedLandingPage === 'linkedin-recruit' ? '✓ Copied!' : 'Copy Post'}
                    </Button>
                  </div>
                  <div className="p-4 bg-slate-50 text-sm text-slate-700 max-h-32 overflow-y-auto whitespace-pre-wrap">
{`🚀 Side Hustle Opportunity: Get Paid to Share AI Tech

What caught my attention:
✅ 20% commission on first sale (up to $1,600 per referral)
✅ $30-75 monthly recurring on subscriptions
✅ Ready-made marketing materials provided

Apply here: https://upost.com/r/${affiliateCode}`}
                  </div>
                </div>

                {/* Facebook Post */}
                <div className="border border-blue-300 rounded-lg overflow-hidden">
                  <div className="bg-blue-600 text-white px-4 py-2 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Facebook className="h-4 w-4" />
                      <span className="font-medium">Facebook</span>
                      <Badge className="bg-blue-700 text-xs">→ Side Hustle Template</Badge>
                    </div>
                    <Button 
                      size="sm"
                      onClick={() => {
                        const recruitUrl = `https://upost.com/r/${affiliateCode}/side-hustle`;
                        const post = `🦥 Looking for a side hustle that's SO EASY even a sloth could do it? 

I just found an opportunity that pays up to $1,600 per referral:

💰 20% commission on sales (that's HUGE)
💰 $30-75 monthly recurring
💰 They give you ALL the marketing materials
💰 No selling required - just share a link

It's helping local businesses get found when people ask ChatGPT for recommendations. Super relevant right now!

Who else is interested? Drop a 🙋‍♂️ below!

Learn more here: ${recruitUrl}

#SideHustle #EasyMoney #WorkFromHome #AffiliateProgram`;
                        navigator.clipboard.writeText(post);
                        setCopiedLandingPage('facebook-recruit');
                        setTimeout(() => setCopiedLandingPage(null), 2000);
                      }}
                      className={copiedLandingPage === 'facebook-recruit' ? 'bg-blue-600' : 'bg-blue-500'}
                    >
                      {copiedLandingPage === 'facebook-recruit' ? '✓ Copied!' : 'Copy Post'}
                    </Button>
                  </div>
                  <div className="p-4 bg-slate-50 text-sm text-slate-700 max-h-32 overflow-y-auto whitespace-pre-wrap">
{`🦥 Looking for a side hustle that's SO EASY even a sloth could do it? 

💰 20% commission on sales (that's HUGE)
💰 $30-75 monthly recurring
💰 They give you ALL the marketing materials

Learn more here: https://upost.com/r/${affiliateCode}/side-hustle`}
                  </div>
                </div>

                {/* Instagram Post */}
                <div className="border border-blue-200 rounded-lg overflow-hidden">
                  <div className="bg-gradient-to-r from-blue-600 via-blue-700 to-cyan-600 text-white px-4 py-2 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Instagram className="h-4 w-4" />
                      <span className="font-medium">Instagram</span>
                      <Badge className="bg-blue-100 text-blue-700 text-xs">Influencer Template</Badge>
                    </div>
                    <Button 
                      size="sm"
                      onClick={() => {
                        const recruitUrl = `https://upost.com/r/${affiliateCode}/influencer`;
                        const post = `🦥💸 EASY SIDE HUSTLE ALERT 💸🦥

Imagine making money while you Netflix...

Just found an affiliate program paying:
• Up to $1,600 per referral 🤑
• $30-75 monthly recurring 💵
• FREE marketing materials 🎁
• No selling - just share links 📲

It's about AI visibility for businesses (super hot right now 🔥)

Perfect if you:
✨ Know business owners
✨ Have a social media following
✨ Want passive income
✨ Love easy money

Link in bio! 👆

${recruitUrl}

#SideHustle #PassiveIncome #AffiliateMarketing #WorkFromHome #EasyMoney`;
                        navigator.clipboard.writeText(post);
                        setCopiedLandingPage('instagram-recruit');
                        setTimeout(() => setCopiedLandingPage(null), 2000);
                      }}
                      className={copiedLandingPage === 'instagram-recruit' ? 'bg-blue-600' : 'bg-blue-600'}
                    >
                      {copiedLandingPage === 'instagram-recruit' ? '✓ Copied!' : 'Copy Post'}
                    </Button>
                  </div>
                  <div className="p-4 bg-slate-50 text-sm text-slate-700 max-h-32 overflow-y-auto whitespace-pre-wrap">
{`🦥💸 EASY SIDE HUSTLE ALERT 💸🦥

• Up to $1,600 per referral 🤑
• $30-75 monthly recurring 💵
• FREE marketing materials 🎁
• No selling - just share links 📲

Link: https://upost.com/r/${affiliateCode}/influencer

#SideHustle #PassiveIncome #AffiliateMarketing`}
                  </div>
                </div>

                {/* Twitter/X Post */}
                <div className="border border-slate-300 rounded-lg overflow-hidden">
                  <div className="bg-black text-white px-4 py-2 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="font-bold">𝕏</span>
                      <span className="font-medium">Twitter/X</span>
                      <Badge className="bg-slate-700 text-xs">→ Money Maker Template</Badge>
                    </div>
                    <Button 
                      size="sm"
                      onClick={() => {
                        const recruitUrl = `https://upost.com/r/${affiliateCode}/money-maker`;
                        const post = `🚀 Found a legit side hustle:

• Up to $1,600/referral
• $30-75 monthly recurring
• They provide ALL marketing materials
• Just share links, no selling

It's for AI visibility tools (huge market rn)

If you know business owners, this is 💰

Apply: ${recruitUrl}`;
                        navigator.clipboard.writeText(post);
                        setCopiedLandingPage('twitter-recruit');
                        setTimeout(() => setCopiedLandingPage(null), 2000);
                      }}
                      className={copiedLandingPage === 'twitter-recruit' ? 'bg-blue-600' : 'bg-slate-700'}
                    >
                      {copiedLandingPage === 'twitter-recruit' ? '✓ Copied!' : 'Copy Post'}
                    </Button>
                  </div>
                  <div className="p-4 bg-slate-50 text-sm text-slate-700 max-h-24 overflow-y-auto whitespace-pre-wrap">
{`🚀 Found a legit side hustle:
• Up to $1,600/referral
• $30-75 monthly recurring
• They provide ALL marketing materials

Apply: https://upost.com/r/${affiliateCode}/money-maker`}
                  </div>
                </div>

                {/* Email Template */}
                <div className="border border-slate-200 rounded-lg overflow-hidden">
                  <div className="bg-slate-700 text-white px-4 py-2 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Mail className="h-4 w-4" />
                      <span className="font-medium">Email Template</span>
                      <Badge className="bg-slate-600 text-xs">Business Pro Template</Badge>
                    </div>
                    <Button 
                      size="sm"
                      onClick={() => {
                        const recruitUrl = `https://upost.com/r/${affiliateCode}/business`;
                        const email = `Subject: Side hustle opportunity - up to $1,600 per referral

Hey [Name],

I wanted to share something I've been doing on the side that's been working well.

UPost has an affiliate program where you can earn:
- 20% commission on first sales (up to $1,600 per referral)
- $30-75 monthly recurring per referral
- PLUS 10% of any affiliates you recruit

They provide all the marketing materials - landing pages, social posts, banners, everything. You literally just share your link.

The product is AI visibility tools for local businesses - helping them show up when people ask ChatGPT for recommendations. It's a growing market.

If you're interested, you can apply here:
${recruitUrl}

Let me know if you have questions!

[Your Name]`;
                        navigator.clipboard.writeText(email);
                        setCopiedLandingPage('email-recruit');
                        setTimeout(() => setCopiedLandingPage(null), 2000);
                      }}
                      className={copiedLandingPage === 'email-recruit' ? 'bg-blue-600' : 'bg-slate-700'}
                    >
                      {copiedLandingPage === 'email-recruit' ? '✓ Copied!' : 'Copy Email'}
                    </Button>
                  </div>
                  <div className="p-4 bg-slate-50 text-sm text-slate-700 max-h-32 overflow-y-auto whitespace-pre-wrap">
{`Subject: Side hustle opportunity - up to $1,600 per referral

Hey [Name],

UPost has an affiliate program:
- 20% commission on first sales
- $30-75 monthly recurring per referral
- PLUS 10% of affiliates you recruit

Apply here: https://upost.com/r/${affiliateCode}/business`}
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Media Kit Tab */}
          <TabsContent value="media-kit" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Image className="h-5 w-5 text-blue-600" />
                  Marketing Materials
                </CardTitle>
                <CardDescription>
                  Download banners, graphics, and templates. All links below are pre-filled with your affiliate code: <strong>{affiliateCode}</strong>
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  {/* Quick Copy Links */}
                  <div>
                    <h3 className="font-semibold text-slate-900 mb-3">Quick Copy Links (Your Code Pre-filled)</h3>
                    <div className="space-y-2">
                      {[
                        { label: 'Homepage', url: `${window.location.origin}/?ref=${affiliateCode}` },
                        { label: 'Dealers Page', url: `${window.location.origin}/dealers?ref=${affiliateCode}` },
                        { label: 'Local Business Page', url: `${window.location.origin}/local-business?ref=${affiliateCode}` },
                        { label: 'AI Visibility Check', url: `${window.location.origin}/?ref=${affiliateCode}#analyzer` },
                      ].map((link, idx) => (
                        <div key={idx} className="flex items-center gap-2 p-2 bg-slate-50 rounded">
                          <span className="text-sm font-medium text-slate-700 w-40">{link.label}:</span>
                          <Input value={link.url} readOnly className="text-xs font-mono flex-1" />
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => {
                              navigator.clipboard.writeText(link.url);
                              alert('Copied!');
                            }}
                          >
                            <Copy className="h-3 w-3" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Email Templates */}
                  <div>
                    <h3 className="font-semibold text-slate-900 mb-3">Email Templates</h3>
                    <div className="bg-slate-50 p-4 rounded-lg">
                      <p className="text-sm text-slate-600 mb-2"><strong>Subject:</strong> Is your website losing you customers?</p>
                      <p className="text-sm text-slate-600">
                        Hi [Name],<br/><br/>
                        I found a great tool that analyzes your website for free and shows exactly what's hurting your rankings and conversions. 
                        I used it myself and was shocked at what I found!<br/><br/>
                        Check it out here: <span className="text-blue-600 font-mono">{window.location.origin}/?ref={affiliateCode}#analyzer</span><br/><br/>
                        Let me know what you think!
                      </p>
                    </div>
                  </div>

                  {/* Full Media Kit Link */}
                  <div className="border-t pt-4">
                    <Link to="/affiliate/media-kit">
                      <Button className="w-full bg-blue-600 hover:bg-blue-700">
                        <FileText className="h-4 w-4 mr-2" />
                        View Full Media Kit with Banners & Graphics
                      </Button>
                    </Link>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Payouts Tab */}
          <TabsContent value="payouts" className="space-y-6">
            {/* Verification Status Banner */}
            {verificationStatus && !verificationStatus.can_receive_payouts && (
              <Card className="bg-slate-50 border-2 border-slate-200">
                <CardContent className="pt-6">
                  <div className="flex items-start gap-4">
                    <AlertCircle className="h-8 w-8 text-blue-600 shrink-0" />
                    <div>
                      <p className="text-lg font-bold text-slate-900 mb-1">Complete Your Payout Setup</p>
                      <p className="text-slate-600 mb-3">
                        Before you can receive commissions, you must complete the following:
                      </p>
                      <ul className="space-y-1 text-sm">
                        <li className={`flex items-center gap-2 ${verificationStatus.w9_uploaded ? 'text-blue-600' : 'text-slate-600'}`}>
                          {verificationStatus.w9_uploaded ? <CheckCircle className="h-4 w-4" /> : <Clock className="h-4 w-4" />}
                          W-9 Tax Form {verificationStatus.w9_uploaded ? '(Uploaded)' : '(Required)'}
                        </li>
                        <li className={`flex items-center gap-2 ${verificationStatus.id_uploaded ? 'text-blue-600' : 'text-slate-600'}`}>
                          {verificationStatus.id_uploaded ? <CheckCircle className="h-4 w-4" /> : <Clock className="h-4 w-4" />}
                          Government ID {verificationStatus.id_uploaded ? '(Uploaded)' : '(Required)'}
                        </li>
                        <li className={`flex items-center gap-2 ${verificationStatus.verification_status === 'verified' ? 'text-blue-600' : 'text-slate-600'}`}>
                          {verificationStatus.verification_status === 'verified' ? <CheckCircle className="h-4 w-4" /> : <Clock className="h-4 w-4" />}
                          Admin Verification {verificationStatus.verification_status === 'verified' ? '(Complete)' : '(Pending Review)'}
                        </li>
                      </ul>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Verified Success Banner */}
            {verificationStatus?.can_receive_payouts && (
              <Card className="bg-blue-50 border-2 border-blue-200">
                <CardContent className="pt-6">
                  <div className="flex items-center gap-4">
                    <CheckCircle className="h-8 w-8 text-blue-600" />
                    <div>
                      <p className="text-lg font-bold text-blue-800">Account Verified!</p>
                      <p className="text-blue-700">Your account is fully set up to receive payouts.</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Earnings Overview */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle>Pending Payout</CardTitle>
                  <CardDescription>Minimum $50 required to request payout</CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="text-4xl font-bold text-blue-600 mb-2">
                    ${stats.stats?.pending_payout?.toFixed(2) || '0.00'}
                  </p>
                  {(stats.stats?.pending_payout || 0) >= 50 ? (
                    <Badge className="bg-blue-600">Ready for payout!</Badge>
                  ) : (
                    <p className="text-sm text-slate-500">
                      ${(50 - (stats.stats?.pending_payout || 0)).toFixed(2)} more to reach minimum
                    </p>
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Earnings Breakdown</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-slate-600">Direct Commissions</span>
                      <span className="font-semibold">${((stats.stats?.total_earned || 0) - teamEarnings - recurringEarnings).toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-600">Recurring (Subscriptions)</span>
                      <span className="font-semibold text-blue-600">${recurringEarnings.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-600">Team Bonus (10%)</span>
                      <span className="font-semibold text-blue-600">${teamEarnings.toFixed(2)}</span>
                    </div>
                    <div className="border-t pt-2 flex justify-between">
                      <span className="font-semibold text-slate-900">Total Earnings</span>
                      <span className="font-bold text-slate-900">${(stats.stats?.total_earned || 0).toFixed(2)}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Request Payout Button - Only shown when fully verified and has balance */}
            {verificationStatus?.can_receive_payouts && (stats.stats?.pending_payout || 0) >= 50 && (
              <Card className="bg-blue-50 border-2 border-blue-200">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <DollarSign className="h-12 w-12 text-blue-600 mr-4" />
                      <div>
                        <p className="text-xl font-bold text-slate-900">Ready to cash out!</p>
                        <p className="text-slate-600">You've reached the $50 minimum and your account is verified</p>
                      </div>
                    </div>
                    <Button 
                      size="lg" 
                      className="bg-blue-600 hover:bg-blue-700"
                      data-testid="request-payout-btn"
                      onClick={async () => {
                        try {
                          const res = await fetch(`${API}/affiliate/request-payout`, {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({
                              affiliate_code: affiliateCode,
                              amount: stats.stats?.pending_payout || 0,
                              payout_method: 'paypal',
                              payout_details: stats.email
                            })
                          });
                          const data = await res.json();
                          if (data.success) {
                            alert(data.message);
                            fetchStats();
                          } else {
                            alert(data.detail || 'Failed to request payout');
                          }
                        } catch (e) {
                          alert('Failed to request payout: ' + e.message);
                        }
                      }}
                    >
                      Request ${stats.stats?.pending_payout?.toFixed(2) || '0.00'} Payout
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Also show request button without verification requirement */}
            {!verificationStatus?.can_receive_payouts && (stats.stats?.pending_payout || 0) >= 50 && (
              <Card className="bg-blue-50 border-2 border-blue-200">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <DollarSign className="h-12 w-12 text-blue-600 mr-4" />
                      <div>
                        <p className="text-xl font-bold text-slate-900">Ready to cash out!</p>
                        <p className="text-slate-600">You have ${stats.stats?.pending_payout?.toFixed(2)} in pending earnings</p>
                      </div>
                    </div>
                    <Button 
                      size="lg" 
                      className="bg-blue-600 hover:bg-blue-700"
                      data-testid="request-payout-btn-unverified"
                      onClick={async () => {
                        try {
                          const res = await fetch(`${API}/affiliate/request-payout`, {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({
                              affiliate_code: affiliateCode,
                              amount: stats.stats?.pending_payout || 0,
                              payout_method: 'paypal',
                              payout_details: stats.email
                            })
                          });
                          const data = await res.json();
                          if (data.success) {
                            alert(data.message);
                            fetchStats();
                          } else {
                            alert(data.detail || 'Failed to request payout');
                          }
                        } catch (e) {
                          alert('Failed to request payout: ' + e.message);
                        }
                      }}
                    >
                      Request ${stats.stats?.pending_payout?.toFixed(2) || '0.00'} Payout
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Payout History */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <DollarSign className="h-5 w-5" />
                  Payout History
                </CardTitle>
              </CardHeader>
              <CardContent>
                <PayoutHistory affiliateCode={affiliateCode} API={API} />
              </CardContent>
            </Card>

            {/* Document Upload Section */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="h-5 w-5" />
                  Required Documents
                </CardTitle>
                <CardDescription>
                  Upload your tax documents and ID to enable payouts. All documents are stored securely.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* W-9 Upload */}
                <div className="border rounded-lg p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <h4 className="font-semibold text-slate-900">W-9 Tax Form</h4>
                        {verificationStatus?.w9_uploaded ? (
                          <Badge className="bg-blue-100 text-blue-700">Uploaded</Badge>
                        ) : (
                          <Badge className="bg-slate-100 text-slate-700">Required</Badge>
                        )}
                      </div>
                      <p className="text-sm text-slate-600 mb-3">
                        Required by the IRS for payments over $600/year. We'll use this to send you a 1099 form at year end.
                      </p>
                      <a 
                        href="https://www.irs.gov/pub/irs-pdf/fw9.pdf" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-sm text-blue-600 hover:underline inline-flex items-center gap-1"
                      >
                        <ExternalLink className="h-3 w-3" />
                        Download blank W-9 from IRS
                      </a>
                    </div>
                    <div className="ml-4">
                      <input
                        type="file"
                        id="w9-upload"
                        accept=".pdf,.jpg,.jpeg,.png"
                        className="hidden"
                        onChange={handleW9Upload}
                        disabled={uploadingW9}
                      />
                      <label htmlFor="w9-upload">
                        <Button 
                          asChild
                          variant={verificationStatus?.w9_uploaded ? "outline" : "default"}
                          className={verificationStatus?.w9_uploaded ? "" : "bg-blue-600 hover:bg-blue-700"}
                          disabled={uploadingW9}
                        >
                          <span className="cursor-pointer">
                            {uploadingW9 ? (
                              <Loader2 className="h-4 w-4 animate-spin mr-2" />
                            ) : (
                              <FileText className="h-4 w-4 mr-2" />
                            )}
                            {verificationStatus?.w9_uploaded ? 'Replace W-9' : 'Upload W-9'}
                          </span>
                        </Button>
                      </label>
                      {verificationStatus?.w9_uploaded_at && (
                        <p className="text-xs text-slate-500 mt-2">
                          Uploaded: {new Date(verificationStatus.w9_uploaded_at).toLocaleDateString()}
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Government ID Upload */}
                <div className="border rounded-lg p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <h4 className="font-semibold text-slate-900">Government-Issued ID</h4>
                        {verificationStatus?.id_uploaded ? (
                          <Badge className="bg-blue-100 text-blue-700">Uploaded</Badge>
                        ) : (
                          <Badge className="bg-slate-100 text-slate-700">Required</Badge>
                        )}
                      </div>
                      <p className="text-sm text-slate-600 mb-1">
                        Upload a clear photo or scan of your driver's license, state ID, or passport.
                      </p>
                      <p className="text-xs text-slate-500">
                        Accepted formats: JPG, PNG, PDF (max 10MB)
                      </p>
                    </div>
                    <div className="ml-4">
                      <input
                        type="file"
                        id="id-upload"
                        accept=".pdf,.jpg,.jpeg,.png"
                        className="hidden"
                        onChange={handleIdUpload}
                        disabled={uploadingId}
                      />
                      <label htmlFor="id-upload">
                        <Button 
                          asChild
                          variant={verificationStatus?.id_uploaded ? "outline" : "default"}
                          className={verificationStatus?.id_uploaded ? "" : "bg-blue-600 hover:bg-blue-700"}
                          disabled={uploadingId}
                        >
                          <span className="cursor-pointer">
                            {uploadingId ? (
                              <Loader2 className="h-4 w-4 animate-spin mr-2" />
                            ) : (
                              <CreditCard className="h-4 w-4 mr-2" />
                            )}
                            {verificationStatus?.id_uploaded ? 'Replace ID' : 'Upload ID'}
                          </span>
                        </Button>
                      </label>
                      {verificationStatus?.id_uploaded_at && (
                        <p className="text-xs text-slate-500 mt-2">
                          Uploaded: {new Date(verificationStatus.id_uploaded_at).toLocaleDateString()}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Payment Method Setup */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CreditCard className="h-5 w-5" />
                  Payment Method
                </CardTitle>
                <CardDescription>
                  Choose how you want to receive your commission payments
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Payment Method Selection */}
                <div className="space-y-3">
                  <label className="block text-sm font-medium text-slate-700">
                    Preferred Payment Method
                  </label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <button
                      onClick={() => setPaymentForm({...paymentForm, preferredMethod: 'paypal'})}
                      className={`p-4 border-2 rounded-lg text-left transition-all ${
                        paymentForm.preferredMethod === 'paypal' 
                          ? 'border-blue-500 bg-blue-50' 
                          : 'border-slate-200 hover:border-blue-300'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center text-white font-bold text-sm">
                          PP
                        </div>
                        <div>
                          <p className="font-semibold text-slate-900">PayPal</p>
                          <p className="text-xs text-slate-500">Instant transfers available</p>
                        </div>
                      </div>
                    </button>
                    
                    <button
                      onClick={() => setPaymentForm({...paymentForm, preferredMethod: 'bank_transfer'})}
                      className={`p-4 border-2 rounded-lg text-left transition-all ${
                        paymentForm.preferredMethod === 'bank_transfer' 
                          ? 'border-blue-500 bg-blue-50' 
                          : 'border-slate-200 hover:border-blue-300'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-slate-700 rounded-full flex items-center justify-center text-white">
                          <DollarSign className="h-5 w-5" />
                        </div>
                        <div>
                          <p className="font-semibold text-slate-900">Bank Transfer</p>
                          <p className="text-xs text-slate-500">Direct deposit (3-5 days)</p>
                        </div>
                      </div>
                    </button>
                  </div>
                </div>

                {/* PayPal Email Input */}
                {paymentForm.preferredMethod === 'paypal' && (
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-slate-700">
                      PayPal Email Address
                    </label>
                    <Input
                      type="email"
                      placeholder="your-paypal@email.com"
                      value={paymentForm.paypalEmail}
                      onChange={(e) => setPaymentForm({...paymentForm, paypalEmail: e.target.value})}
                    />
                    <p className="text-xs text-slate-500">
                      We'll send payments to this PayPal account
                    </p>
                  </div>
                )}

                {/* Bank Transfer Info */}
                {paymentForm.preferredMethod === 'bank_transfer' && (
                  <div className="bg-slate-50 rounded-lg p-4">
                    <p className="text-sm text-slate-600">
                      For bank transfers, please contact us at <a href="mailto:affiliates@upost.com" className="text-blue-600 hover:underline">affiliates@upost.com</a> with your banking details. We'll set up direct deposit for your payouts.
                    </p>
                  </div>
                )}

                {/* Save Button */}
                <Button 
                  onClick={handleSavePaymentInfo}
                  disabled={savingPayment || (paymentForm.preferredMethod === 'paypal' && !paymentForm.paypalEmail.trim())}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  {savingPayment ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : (
                    <Save className="h-4 w-4 mr-2" />
                  )}
                  Save Payment Info
                </Button>

                {verificationStatus?.paypal_info && (
                  <div className="flex items-center gap-2 text-sm text-blue-600">
                    <CheckCircle className="h-4 w-4" />
                    PayPal connected: {verificationStatus.paypal_info}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Payout Schedule Info */}
            <Card>
              <CardHeader>
                <CardTitle>Payout Schedule</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <Clock className="h-5 w-5 text-slate-400 mt-0.5" />
                    <div>
                      <p className="font-medium text-slate-900">Monthly Payouts</p>
                      <p className="text-sm text-slate-600">
                        Payouts are processed on the 1st and 15th of each month for balances over $100.
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <DollarSign className="h-5 w-5 text-slate-400 mt-0.5" />
                    <div>
                      <p className="font-medium text-slate-900">Minimum Payout: $100</p>
                      <p className="text-sm text-slate-600">
                        Commissions below $100 will roll over to the next payout period.
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <FileText className="h-5 w-5 text-slate-400 mt-0.5" />
                    <div>
                      <p className="font-medium text-slate-900">Tax Reporting</p>
                      <p className="text-sm text-slate-600">
                        You'll receive a 1099 form by January 31st for earnings over $600 in the previous year.
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

export default AffiliateDashboard;
